import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { fmtDateTime, fmtTime } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Video, ArrowLeft, RefreshCcw, FileDown, X, MapPin, Copy, FileText,
  CheckCircle2, AlertCircle, ImagePlus, MessageSquare, Sparkles, Brain, Loader2,
  Calculator, Trash2, UploadCloud, ShieldCheck, ChevronDown, Ruler,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { AiInstructionsPanel } from "@/components/siniestro/AiInstructionsPanel";
import { BaremoCombobox } from "@/components/siniestro/BaremoCombobox";
import { PhotoMeasureDialog } from "@/components/photo/PhotoMeasureDialog";
import { PhotoRoomSelector } from "@/components/photo/PhotoRoomSelector";
import { RoomSummariesPanel } from "@/components/photo/RoomSummariesPanel";

const AI_NOTES_LABELS = [
  ["tipo_siniestro", "Tipo de siniestro"],
  ["ramo", "Ramo"],
  ["objeto_asegurado", "Objeto asegurado"],
  ["estancia", "Estancia / ubicación"],
  ["causa_probable", "Causa probable"],
  ["elementos_danados", "Elementos dañados"],
  ["intervencion_urgente", "Intervención urgente"],
  ["afectaciones_terceros", "Daños a terceros"],
  ["direccion_siniestro", "Dirección"],
  ["fecha_siniestro", "Fecha del siniestro"],
  ["valoracion_aprox", "Valoración aprox."],
  ["compania_aseguradora", "Compañía"],
  ["poliza", "Póliza"],
  ["observaciones", "Observaciones"],
];

/* ---------- DropZone reutilizable: PDF drag&drop con click para seleccionar ---------- */
function DropZone({ label, hint, accept = ".pdf", busy = false, onFile, testid, dense = false, multiple = false }) {
  const inputRef = React.useRef(null);
  const [hover, setHover] = React.useState(false);
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setHover(false);
    if (busy) return;
    const files = e.dataTransfer?.files;
    if (!files || !files.length) return;
    if (multiple) {
      Array.from(files).forEach((f) => onFile?.(f));
    } else {
      onFile?.(files[0]);
    }
  };
  const onSelect = (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    if (multiple) {
      Array.from(files).forEach((f) => onFile?.(f));
    } else {
      onFile?.(files[0]);
    }
    e.target.value = "";
  };
  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); setHover(true); }}
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={onDrop}
      onClick={() => !busy && inputRef.current?.click()}
      className={`relative border-2 border-dashed rounded-[14px] ${dense ? "p-3" : "p-5"} text-center cursor-pointer transition-colors ${
        hover ? "border-[#D85A30] bg-[#FFF3EE]" : "border-[#E5E2D8] bg-[#FBFAF6] hover:border-[#D85A30] hover:bg-[#FFF7F2]"
      } ${busy ? "opacity-60 cursor-wait" : ""}`}
      data-testid={testid}
    >
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} className="hidden" onChange={onSelect} />
      {busy ? (
        <div className="flex items-center justify-center gap-2 text-[#D85A30] text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Procesando con IA…
        </div>
      ) : (
        <>
          <UploadCloud className={`mx-auto ${dense ? "h-5 w-5" : "h-6 w-6"} text-[#7A786F] mb-1`} />
          <div className="text-sm font-medium text-[#2C2C2A]">{label}</div>
          {hint && <div className="text-[11px] text-[#7A786F] mt-0.5">{hint}</div>}
          <div className="text-[10px] uppercase tracking-wide text-[#7A786F] mt-1">Arrastra y suelta o haz click</div>
        </>
      )}
    </div>
  );
}

/* DropZone específico de Preexistencia, con badge "OK" y resumen del archivo subido */
function PreexDropZone({ label, hint, file, busy, onFile, testid, extracted, ok }) {
  return (
    <div className="space-y-1">
      <DropZone label={label} hint={hint} onFile={onFile} busy={busy} testid={testid} accept=".pdf" dense />
      {file && (
        <div className="text-[11px] text-[#7A786F] px-1 flex items-center gap-1 flex-wrap" data-testid={`${testid}-uploaded`}>
          {ok ? <CheckCircle2 className="h-3 w-3 text-[#1D9E75]" /> : <AlertCircle className="h-3 w-3 text-[#D85A30]" />}
          <span className="font-mono truncate max-w-[16rem]">{file.filename}</span>
          <span className="opacity-70">· {(file.size_bytes / 1024).toFixed(0)} KB</span>
          {file.uploaded_at && (
            <span className="opacity-70">· {new Date(file.uploaded_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}</span>
          )}
        </div>
      )}
      {extracted?.notas && (
        <div className="text-[11px] text-[#D85A30] px-1">⚠️ {extracted.notas}</div>
      )}
    </div>
  );
}

export default function SiniestroDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiNotes, setAiNotes] = useState(null);
  const [aiNarrative, setAiNarrative] = useState("");
  const [aiNarrativeEdited, setAiNarrativeEdited] = useState(false);
  const [aiUpdatedAt, setAiUpdatedAt] = useState(null);
  const [aiTranscriptChars, setAiTranscriptChars] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const [narrativeDraft, setNarrativeDraft] = useState("");
  // Instrucciones libres del tramitador para la IA del expediente.
  // Hay DOS bloques independientes: causas y valoración.
  const [aiInstrCausas, setAiInstrCausas] = useState({ text: "", updated_at: null, updated_by: null });
  const [aiInstrValoracion, setAiInstrValoracion] = useState({ text: "", updated_at: null, updated_by: null });
  const [aiInstrCausasDraft, setAiInstrCausasDraft] = useState("");
  const [aiInstrValoracionDraft, setAiInstrValoracionDraft] = useState("");
  const [aiInstructionsSaving, setAiInstructionsSaving] = useState(false);
  const [aiInstrCausasRecording, setAiInstrCausasRecording] = useState(false);
  const [aiInstrCausasTranscribing, setAiInstrCausasTranscribing] = useState(false);
  const [aiInstrValoracionRecording, setAiInstrValoracionRecording] = useState(false);
  const [aiInstrValoracionTranscribing, setAiInstrValoracionTranscribing] = useState(false);
  const _aiInstrCausasRecRef = React.useRef(null);
  const _aiInstrCausasChunksRef = React.useRef([]);
  const _aiInstrValoracionRecRef = React.useRef(null);
  const _aiInstrValoracionChunksRef = React.useRef([]);
  const [valoracion, setValoracion] = useState(null);
  const [valoracionDraft, setValoracionDraft] = useState(null);
  const [valoracionEdited, setValoracionEdited] = useState(false);
  const [valoracionUpdatedAt, setValoracionUpdatedAt] = useState(null);
  const [valoracionLoading, setValoracionLoading] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  // Partidas del baremo asignado al expediente (para el combobox de valoración)
  const [baremoItems, setBaremoItems] = useState([]);
  // Toggle de aplicación del infraseguro por categoría
  const [applyInfraCont, setApplyInfraCont] = useState(true);
  const [applyInfraContenido, setApplyInfraContenido] = useState(true);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [measurePhoto, setMeasurePhoto] = useState(null); // foto en la que se abre el diálogo de medición IA
  const [roomCatalog, setRoomCatalog] = useState([]);
  const [docUploadingTel, setDocUploadingTel] = useState(false);
  const docInputRef = React.useRef(null);

  // === Preexistencia IA ===
  const [preex, setPreex] = useState(null); // { catastro_text, extracted, inputs, result, updated_at }
  const [preexLoading, setPreexLoading] = useState(false);
  const [preexCatastroText, setPreexCatastroText] = useState("");
  const [preexForm, setPreexForm] = useState({
    calidad: "Estándar",
    tipo_inmueble: "Piso",
    producto: "1610",
    fecha_contratacion: "",
    capital_continente: 0,
    capital_contenido: 0,
    m2_declarados_poliza: 0,
    uso: "Principal",
    ocupantes: 1,
    danno_eur: 0,
    franquicia_eur: 0,
    tipo_danno: "continente",
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/siniestros/${id}`);
      setData(r.data);
      const ai = r.data?.ai_instructions;
      if (ai && typeof ai === "object") {
        // Compatibilidad con datos previos al split: si solo viene `text`
        // (sin causas_text/valoracion_text) lo tratamos como causas — era el
        // caso de uso original.
        const causasTxt = ai.causas_text ?? (ai.valoracion_text ? "" : (ai.text || ""));
        const valoracionTxt = ai.valoracion_text || "";
        setAiInstrCausas({ text: causasTxt, updated_at: ai.updated_at || null, updated_by: ai.updated_by || null });
        setAiInstrValoracion({ text: valoracionTxt, updated_at: ai.updated_at || null, updated_by: ai.updated_by || null });
        setAiInstrCausasDraft(causasTxt);
        setAiInstrValoracionDraft(valoracionTxt);
      } else {
        setAiInstrCausas({ text: "", updated_at: null, updated_by: null });
        setAiInstrValoracion({ text: "", updated_at: null, updated_by: null });
        setAiInstrCausasDraft("");
        setAiInstrValoracionDraft("");
      }
    } catch (e) {
      toast.error("No se pudo cargar el siniestro");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Cargar catálogo de estancias una sola vez
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/rooms/catalog");
        setRoomCatalog(r.data?.items || []);
      } catch (_) { /* no-op */ }
    })();
  }, []);

  // Cargar notas IA al entrar
  useEffect(() => {
    api.get(`/siniestros/${id}/ai-notes`).then(r => {
      if (r.data?.notes && Object.keys(r.data.notes).length) setAiNotes(r.data.notes);
      setAiNarrative(r.data?.narrative || "");
      setNarrativeDraft(r.data?.narrative || "");
      setAiNarrativeEdited(!!r.data?.narrative_edited);
      setAiUpdatedAt(r.data?.updated_at || null);
      setAiTranscriptChars(r.data?.transcript_chars || 0);
      const val = r.data?.valoracion || null;
      setValoracion(val);
      setValoracionDraft(normalizeValoracion(val));
      setValoracionEdited(!!r.data?.valoracion_edited);
      setValoracionUpdatedAt(r.data?.valoracion_updated_at || null);
    }).catch(() => {});
    // Cargar partidas del baremo asignado al expediente (para el combobox)
    api.get(`/siniestros/${id}/baremo-items`).then(r => {
      setBaremoItems(r.data?.items || []);
    }).catch(() => { setBaremoItems([]); });
  }, [id]);

  // Cargar preexistencia previa
  useEffect(() => {
    api.get(`/siniestros/${id}/preexistencia`).then(r => {
      const px = r.data && Object.keys(r.data).length ? r.data : null;
      if (px) {
        setPreex(px);
        setPreexCatastroText(px.catastro_text || "");
        const mi = px.manual_inputs || {};
        const ext = px.extracted || {};
        setPreexForm((f) => ({
          ...f,
          calidad: mi.calidad || f.calidad,
          // tipo_inmueble: prioriza el manual; si no, lo extraído del catastro;
          // si no, dejamos "Piso" como default (más común en España).
          tipo_inmueble: mi.tipo_inmueble || ext.tipo_inmueble || f.tipo_inmueble || "Piso",
          producto: mi.producto || f.producto,
          fecha_contratacion: mi.fecha_contratacion || "",
          capital_continente: Number(mi.capital_continente || 0),
          capital_contenido: Number(mi.capital_contenido || 0),
          m2_declarados_poliza: Number(mi.m2_declarados_poliza || 0),
          uso: mi.uso || f.uso,
          ocupantes: Number(mi.ocupantes || 1),
          danno_eur: Number(mi.danno_eur || 0),
          franquicia_eur: Number(mi.franquicia_eur || 0),
          tipo_danno: mi.tipo_danno || "continente",
        }));
      }
    }).catch(() => {});
  }, [id]);

  const calcularPreexistencia = async () => {
    if (!preexCatastroText.trim() && !preex?.extracted) {
      toast.error("Pega antes el texto descriptivo del catastro.");
      return;
    }
    setPreexLoading(true);
    try {
      const body = { ...preexForm, catastro_text: preexCatastroText.trim() };
      const r = await api.post(`/siniestros/${id}/preexistencia`, body, { timeout: 60_000 });
      setPreex(r.data);
      toast.success("Preexistencia calculada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al calcular la preexistencia");
    } finally {
      setPreexLoading(false);
    }
  };

  // === Subidas de descripción catastral y apertura ===
  const [preexUpCat, setPreexUpCat] = useState(false);
  const [preexUpApe, setPreexUpApe] = useState(false);

  const _absorbPreexResponse = (data) => {
    setPreex(data);
    const mi = data.manual_inputs || {};
    const ext = data.extracted || {};
    setPreexForm((f) => ({
      ...f,
      calidad: mi.calidad || f.calidad,
      tipo_inmueble: mi.tipo_inmueble || ext.tipo_inmueble || f.tipo_inmueble || "Piso",
      producto: mi.producto || f.producto,
      fecha_contratacion: mi.fecha_contratacion || f.fecha_contratacion,
      capital_continente: Number(mi.capital_continente || 0),
      capital_contenido: Number(mi.capital_contenido || 0),
      m2_declarados_poliza: Number(mi.m2_declarados_poliza || 0),
      uso: mi.uso || f.uso,
      ocupantes: Number(mi.ocupantes || 1),
      danno_eur: Number(mi.danno_eur || 0),
      franquicia_eur: Number(mi.franquicia_eur || 0),
      tipo_danno: mi.tipo_danno || f.tipo_danno,
    }));
  };

  const uploadCatastroFile = async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      toast.error("Solo se admite PDF.");
      return;
    }
    setPreexUpCat(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/siniestros/${id}/preexistencia/upload-catastro`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      _absorbPreexResponse(r.data);
      toast.success(r.data?.result ? "Catastro procesado y preexistencia calculada" : "Catastro procesado. Sube la apertura o completa los capitales para calcular.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error subiendo la descripción catastral");
    } finally {
      setPreexUpCat(false);
    }
  };

  const uploadAperturaFile = async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      toast.error("Solo se admite PDF.");
      return;
    }
    setPreexUpApe(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/siniestros/${id}/preexistencia/upload-apertura`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      _absorbPreexResponse(r.data);
      toast.success(r.data?.result ? "Apertura procesada y preexistencia calculada" : "Apertura procesada. Sube el catastro para calcular.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error subiendo la apertura");
    } finally {
      setPreexUpApe(false);
    }
  };

  // === Instrucciones para la IA (dos bloques separados) ===
  const guardarAiInstructions = async () => {
    setAiInstructionsSaving(true);
    try {
      const r = await api.put(`/siniestros/${id}/ai-instructions`, {
        causas_text: aiInstrCausasDraft,
        valoracion_text: aiInstrValoracionDraft,
      });
      const ai = r.data?.ai_instructions;
      if (ai) {
        setAiInstrCausas({ text: ai.causas_text || "", updated_at: ai.updated_at, updated_by: ai.updated_by });
        setAiInstrValoracion({ text: ai.valoracion_text || "", updated_at: ai.updated_at, updated_by: ai.updated_by });
        toast.success("Instrucciones guardadas. Se aplicarán a las siguientes generaciones IA.");
      } else {
        setAiInstrCausas({ text: "", updated_at: null, updated_by: null });
        setAiInstrValoracion({ text: "", updated_at: null, updated_by: null });
        toast.success("Instrucciones eliminadas.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron guardar las instrucciones");
    } finally {
      setAiInstructionsSaving(false);
    }
  };

  // Helper genérico para grabar + transcribir un bloque de instrucciones IA.
  // Recibe los setters/refs del bloque (causas o valoración) y los reutiliza
  // para evitar duplicar 70 líneas de lógica de MediaRecorder.
  const _makeInstrRecording = ({
    recRef, chunksRef, setRecording, setTranscribing, appendToDraft,
  }) => ({
    start: async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mr = new MediaRecorder(stream, {
          mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm",
        });
        chunksRef.current = [];
        mr.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunksRef.current.push(ev.data); };
        mr.onstop = async () => {
          try { stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* no-op */ }
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          if (!blob || blob.size < 800) {
            toast.error("Grabación demasiado corta.");
            return;
          }
          setTranscribing(true);
          try {
            const fd = new FormData();
            fd.append("file", blob, "instr.webm");
            const r = await api.post(`/siniestros/${id}/transcribe-only`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 60_000,
            });
            const text = (r.data?.text || "").trim();
            if (!text) {
              toast.error("No se ha podido transcribir el audio.");
              return;
            }
            appendToDraft(text);
            toast.success("Audio transcrito. Revisa y pulsa Guardar.");
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Error transcribiendo");
          } finally {
            setTranscribing(false);
          }
        };
        mr.start();
        recRef.current = mr;
        setRecording(true);
      } catch (err) {
        toast.error("No se pudo acceder al micrófono.");
      }
    },
    stop: () => {
      const mr = recRef.current;
      if (mr && mr.state !== "inactive") mr.stop();
      recRef.current = null;
      setRecording(false);
    },
  });

  const _instrCausasRec = _makeInstrRecording({
    recRef: _aiInstrCausasRecRef,
    chunksRef: _aiInstrCausasChunksRef,
    setRecording: setAiInstrCausasRecording,
    setTranscribing: setAiInstrCausasTranscribing,
    appendToDraft: (t) => setAiInstrCausasDraft((prev) => prev ? (prev.trimEnd() + " " + t) : t),
  });
  const _instrValoracionRec = _makeInstrRecording({
    recRef: _aiInstrValoracionRecRef,
    chunksRef: _aiInstrValoracionChunksRef,
    setRecording: setAiInstrValoracionRecording,
    setTranscribing: setAiInstrValoracionTranscribing,
    appendToDraft: (t) => setAiInstrValoracionDraft((prev) => prev ? (prev.trimEnd() + " " + t) : t),
  });

  const eliminarPreexistencia = async () => {
    if (!preex) return;
    if (!window.confirm("¿Eliminar la preexistencia guardada de este expediente?")) return;
    try {
      await api.delete(`/siniestros/${id}/preexistencia`);
      setPreex(null);
      setPreexCatastroText("");
      toast.success("Preexistencia eliminada");
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const copiarInformePreex = () => {
    if (!preex?.result?.informe_texto) return;
    copyText(preex.result.informe_texto, "Informe de preexistencia copiado");
  };

  // Helper interno: para POST a /ai-notes con tolerancia a timeouts de
  // Cloudflare (~100 s). Si la red cae mientras el backend sigue procesando,
  // hacemos polling a /ai-notes (GET) hasta detectar la finalización.
  const _aiNotesWithPolling = async (postUrl, postBody, requestConfig = {}) => {
    let baselineUpdatedAt = null;
    try {
      const before = await api.get(`/siniestros/${id}/ai-notes`).catch(() => null);
      baselineUpdatedAt = before?.data?.updated_at || null;
    } catch (_) { /* sin baseline */ }
    try {
      const r = await api.post(postUrl, postBody, { timeout: 180_000, ...requestConfig });
      return { ok: true, data: r.data, recovered: false };
    } catch (e) {
      const isTimeoutOrGateway =
        e?.code === "ECONNABORTED" ||
        !e?.response ||
        [502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527].includes(e?.response?.status);
      if (!isTimeoutOrGateway) return { ok: false, error: e };
      toast.message("El procesado está tardando más de lo habitual, espera unos segundos…");
      for (let i = 0; i < 36; i++) { // 3 min
        await new Promise(rr => setTimeout(rr, 5000));
        try {
          const r2 = await api.get(`/siniestros/${id}/ai-notes`);
          const updated = r2.data?.updated_at;
          const hasContent = !!(r2.data?.narrative || r2.data?.notes);
          if (hasContent && updated !== baselineUpdatedAt) {
            return { ok: true, data: r2.data, recovered: true };
          }
        } catch (_) { /* sigue intentando */ }
      }
      return { ok: false, error: e };
    }
  };

  const refreshAiNotes = async () => {
    setAiLoading(true);
    try {
      const res = await _aiNotesWithPolling(`/siniestros/${id}/ai-notes`);
      if (!res.ok) {
        toast.error("No se pudo actualizar. Refresca el expediente en unos minutos.");
        return;
      }
      const d = res.data;
      setAiNotes(d?.notes || null);
      setAiNarrative(d?.narrative || "");
      setNarrativeDraft(d?.narrative || "");
      setAiNarrativeEdited(!!d?.narrative_edited);
      setAiUpdatedAt(d?.updated_at || null);
      setAiTranscriptChars(d?.transcript_chars || 0);
      toast.success(res.recovered ? "Notas IA actualizadas (tras tiempo de espera de la red)" : "Notas IA actualizadas");
    } finally {
      setAiLoading(false);
    }
  };

  const saveNarrative = async () => {
    setAiLoading(true);
    try {
      const r = await api.put(`/siniestros/${id}/ai-narrative`, { value: narrativeDraft });
      setAiNarrative(r.data?.narrative || "");
      setAiNarrativeEdited(!!r.data?.narrative_edited);
      setAiUpdatedAt(r.data?.updated_at || null);
      toast.success("Texto guardado");
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setAiLoading(false);
    }
  };

  const regenerateNarrative = async () => {
    setAiLoading(true);
    try {
      await api.put(`/siniestros/${id}/ai-narrative`, { value: null });
      const res = await _aiNotesWithPolling(`/siniestros/${id}/ai-notes`);
      if (!res.ok) {
        toast.error("No se pudo regenerar. Refresca el expediente en unos minutos.");
        return;
      }
      const d = res.data;
      setAiNotes(d?.notes || null);
      setAiNarrative(d?.narrative || "");
      setNarrativeDraft(d?.narrative || "");
      setAiNarrativeEdited(false);
      setAiUpdatedAt(d?.updated_at || null);
      toast.success(res.recovered ? "Texto regenerado (tras tiempo de espera de la red)" : "Texto regenerado desde IA");
    } finally {
      setAiLoading(false);
    }
  };

  // Línea informativa con la modalidad de póliza suscrita (si está fijada)
  const policyHeader = () => {
    const name = data?.siniestro?.policy_template_name;
    if (!name || name === "Otra póliza") return "";
    return `Modalidad de póliza suscrita: ${name.toUpperCase()}`;
  };

  const copyNarrative = async () => {
    try {
      const header = policyHeader();
      const body = aiNarrative || "";
      const text = header ? `${header}\n\n${body}` : body;
      await navigator.clipboard.writeText(text);
      toast.success("Texto copiado al portapapeles");
    } catch (e) { toast.error("No se pudo copiar"); }
  };

  // === Valoración sugerida (Phase 2) ===========================================
  const generateValoracion = async () => {
    setValoracionLoading(true);
    // Baseline para detectar más tarde si el backend terminó aunque el cliente
    // no recibiera la respuesta (caso típico: Cloudflare corta a ~100 s).
    let baselineUpdatedAt = null;
    try {
      const before = await api.get(`/siniestros/${id}/ai-notes`).catch(() => null);
      baselineUpdatedAt = before?.data?.updated_at || before?.data?.valoracion_updated_at || null;
    } catch (_) { /* sin baseline, seguimos */ }

    const applyResponse = (r) => {
      const val = r.data?.valoracion || null;
      const errMsg = r.data?.valoracion_error || val?._error;
      setValoracion(val);
      setValoracionDraft(normalizeValoracion(val));
      setValoracionEdited(!!r.data?.valoracion_edited);
      setValoracionUpdatedAt(r.data?.valoracion_updated_at || null);
      if (r.data?.narrative != null) {
        setAiNarrative(r.data.narrative);
        if (!aiNarrativeEdited) setNarrativeDraft(r.data.narrative);
        setAiNarrativeEdited(!!r.data?.narrative_edited);
      }
      const partidasCount = (val?.partidas || []).length;
      if (partidasCount > 0) {
        const nfot = val.fotos_analizadas || 0;
        toast.success(
          nfot > 0
            ? `Valoración generada (${nfot} fotos analizadas, ${partidasCount} partidas)`
            : `Valoración generada solo con texto (${partidasCount} partidas)`,
        );
      } else if (errMsg) {
        toast.error(`No se pudo generar la valoración: ${errMsg}`);
      } else {
        toast("Sin valoración: la IA no pudo identificar partidas valorables. Revisa la narrativa o añade más fotos.");
      }
    };

    try {
      const r = await api.post(`/siniestros/${id}/ai-report`, null, { timeout: 180_000 });
      applyResponse(r);
    } catch (e) {
      // Si Cloudflare corta antes de tiempo (524/520, response inválida) o
      // tenemos ECONNABORTED por timeout cliente, el backend SIGUE procesando.
      // Hacemos polling a /ai-notes durante 3 min para detectar la finalización.
      const isTimeoutOrGateway =
        e?.code === "ECONNABORTED" ||
        !e?.response ||
        [502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527].includes(e?.response?.status);
      if (isTimeoutOrGateway) {
        toast.message("El procesado está tardando más de lo habitual, espera unos segundos…");
        let recovered = false;
        for (let i = 0; i < 36; i++) { // 36 × 5s = 3 min
          await new Promise(r => setTimeout(r, 5000));
          try {
            const r2 = await api.get(`/siniestros/${id}/ai-notes`);
            const updated = r2.data?.updated_at || r2.data?.valoracion_updated_at;
            const hasContent = !!(r2.data?.narrative || r2.data?.valoracion);
            if (hasContent && updated !== baselineUpdatedAt) {
              // Recargamos los datos completos del expediente
              await load();
              toast.success("Valoración generada (recuperada tras tiempo de espera de la red)");
              recovered = true;
              break;
            }
          } catch (_) { /* reintentamos */ }
        }
        if (!recovered) {
          toast.error("La IA está tardando más de lo esperado. Refresca el expediente en unos minutos para ver si terminó.");
        }
      } else {
        const detail = e?.response?.data?.detail || e?.message || "Error desconocido";
        toast.error(`No se pudo generar la valoración: ${detail}`);
      }
    } finally {
      setValoracionLoading(false);
    }
  };

  const saveValoracion = async () => {
    setValoracionLoading(true);
    try {
      const r = await api.put(`/siniestros/${id}/ai-valoracion`, { value: valoracionDraft });
      const val = r.data?.valoracion || null;
      setValoracion(val);
      setValoracionDraft(normalizeValoracion(val));
      setValoracionEdited(!!r.data?.valoracion_edited);
      setValoracionUpdatedAt(r.data?.valoracion_updated_at || null);
      toast.success("Valoración guardada");
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setValoracionLoading(false);
    }
  };

  const discardValoracionEdits = () => {
    setValoracionDraft(normalizeValoracion(valoracion));
  };

  // Normaliza una valoración (sea recién generada por la IA o cargada de la BD
  // potencialmente con la forma antigua) al esquema tabular nuevo: garantiza
  // unidades / precio_unidad_eur / depreciacion_pct / mercado_pct / iva_pct /
  // total_partida_eur en cada partida y recalcula totales. (Definida más abajo
  // tras los helpers para evitar referencias circulares.)
  // === Helpers para la nueva valoración tabular ============================
  // Una partida tiene: baremo_code, concepto, tipo, metodo, unidades,
  // precio_unidad_eur, depreciacion_pct, mercado_pct, iva_pct, total_partida_eur.
  const _toNum = (v, d = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };
  const recalcPartida = (p) => {
    const uds = _toNum(p.unidades, 0);
    const eur_u = _toNum(p.precio_unidad_eur, 0);
    const dep = _toNum(p.depreciacion_pct, 0);
    const mkt = _toNum(p.mercado_pct, 0);
    const iva = _toNum(p.iva_pct, 0);
    const total = Math.round(uds * eur_u * (1 - dep / 100) * (1 + mkt / 100) * (1 + iva / 100) * 100) / 100;
    return { ...p, total_partida_eur: total, estimado_eur: total };
  };
  const recalcTotals = (next) => {
    next.partidas = (next.partidas || []).map(recalcPartida);
    const total = next.partidas.reduce((acc, p) => acc + (_toNum(p.total_partida_eur, 0)), 0);
    // Subtotal sin IVA = total / (1 + iva_pct_global/100). Asumimos que todas las partidas
    // comparten el mismo % de IVA (caso normal: 0 ó 21).
    const ivaPcts = next.partidas.map((p) => _toNum(p.iva_pct, 0));
    const ivaUniforme = ivaPcts.length && ivaPcts.every((v) => v === ivaPcts[0]) ? ivaPcts[0] : null;
    next.total_estimado_eur = total ? Math.round(total * 100) / 100 : null;
    if (ivaUniforme && ivaUniforme > 0) {
      const subtotal = total / (1 + ivaUniforme / 100);
      next.subtotal_sin_iva = Math.round(subtotal * 100) / 100;
      next.iva_eur = Math.round((total - subtotal) * 100) / 100;
      next.iva_aplicado = true;
      next.iva_pct_global = ivaUniforme;
    } else {
      next.subtotal_sin_iva = null;
      next.iva_eur = null;
      next.iva_aplicado = false;
      next.iva_pct_global = 0;
    }
    // Recalcular sublímites aplicados: por cada límite, recomputamos `bruto_eur`
    // a partir de las partidas que sigan existiendo (descartando índices fuera
    // de rango) y `importe_aplicado` = min(bruto, limite_eur). Esto cubre el
    // caso reportado por el usuario: cambiar/eliminar partidas debe propagar
    // al neto a indemnizar.
    const limites = Array.isArray(next.limites_aplicados) ? next.limites_aplicados : [];
    let ahorroSublimites = 0;
    next.limites_aplicados = limites
      .map((lim) => {
        const partidasIdx = Array.isArray(lim.partidas_afectadas) ? lim.partidas_afectadas : [];
        const idxValidos = partidasIdx.filter((i) => Number.isInteger(i) && i >= 0 && i < next.partidas.length);
        const bruto = Math.round(idxValidos.reduce((acc, i) => acc + _toNum(next.partidas[i]?.total_partida_eur, 0), 0) * 100) / 100;
        const limite = _toNum(lim.limite_eur, 0);
        // Aplicado = min(bruto, límite). Si no hay partidas afectadas el límite
        // se queda en 0 y no genera ahorro (lo dejamos visible para que el
        // perito decida si lo quita a mano).
        const aplicado = Math.round(Math.min(bruto, limite || bruto) * 100) / 100;
        if (limite > 0 && bruto > limite) ahorroSublimites += bruto - aplicado;
        return {
          ...lim,
          partidas_afectadas: idxValidos,
          bruto_eur: bruto,
          importe_aplicado: aplicado,
        };
      });
    // total_neto = max(0, total_estimado − ahorro_sublímites − franquicia).
    const franq = _toNum(next.franquicia_eur, 0);
    if (next.total_estimado_eur == null) {
      next.total_neto_eur = null;
    } else {
      const neto = Math.max(0, next.total_estimado_eur - ahorroSublimites - franq);
      next.total_neto_eur = Math.round(neto * 100) / 100;
    }
    return next;
  };
  const normalizeValoracion = (val) => {
    if (!val) return null;
    const v = JSON.parse(JSON.stringify(val));
    v.partidas = (v.partidas || []).map((p) => {
      const np = { ...p };
      if (!np._key) {
        np._key = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `p-${Math.random().toString(36).slice(2, 10)}`;
      }
      const legacy = Number(p.estimado_eur);
      if (np.unidades == null) np.unidades = 1;
      if (np.precio_unidad_eur == null) {
        // Si solo había estimado_eur, lo trasladamos al precio unitario.
        np.precio_unidad_eur = Number.isFinite(legacy) ? legacy : 0;
      }
      if (np.depreciacion_pct == null) np.depreciacion_pct = 0;
      if (np.mercado_pct == null) np.mercado_pct = 0;
      if (np.iva_pct == null) np.iva_pct = 0;
      return np;
    });
    return recalcTotals(v);
  };

  const updatePartida = (idx, key, value) => {
    setValoracionDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.partidas = next.partidas || [];
      if (!next.partidas[idx]) next.partidas[idx] = {};
      next.partidas[idx][key] = value;
      return recalcTotals(next);
    });
  };

  const removePartida = (idx) => {
    setValoracionDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.partidas = (next.partidas || []).filter((_, i) => i !== idx);
      // Reindexa los sublímites: descartamos el índice eliminado y decrementamos
      // los posteriores. Si un sublímite se queda sin partidas afectadas, lo
      // dejamos en la lista con bruto=0/aplicado=0 — el perito puede quitarlo
      // a mano si ya no aplica.
      if (Array.isArray(next.limites_aplicados)) {
        next.limites_aplicados = next.limites_aplicados.map((lim) => {
          const oldIdx = Array.isArray(lim.partidas_afectadas) ? lim.partidas_afectadas : [];
          const newIdx = oldIdx
            .filter((i) => i !== idx)
            .map((i) => (i > idx ? i - 1 : i));
          return { ...lim, partidas_afectadas: newIdx };
        });
      }
      return recalcTotals(next);
    });
  };

  const addPartida = () => {
    setValoracionDraft((prev) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : { partidas: [], total_estimado_eur: null, rango_estimado: null, observaciones: null, fotos_analizadas: 0, calidad_evaluacion: null };
      next.partidas = next.partidas || [];
      next.partidas.push({
        _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        baremo_code: null,
        concepto: "",
        tipo: null,
        metodo: null,
        unidades: 1,
        precio_unidad_eur: 0,
        depreciacion_pct: 0,
        mercado_pct: 0,
        iva_pct: 0,
        total_partida_eur: 0,
        estimado_eur: 0,
        sin_baremo: true,
      });
      return recalcTotals(next);
    });
  };

  const updateValoracionField = (key, value) => {
    setValoracionDraft((prev) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : { partidas: [], total_estimado_eur: null, rango_estimado: null, observaciones: null, fotos_analizadas: 0, calidad_evaluacion: null };
      next[key] = value;
      return next;
    });
  };

  const sumPartidasAsTotal = () => {
    setValoracionDraft((prev) => {
      if (!prev) return prev;
      return recalcTotals(JSON.parse(JSON.stringify(prev)));
    });
  };

  // +15 % precio de mercado en TODAS las partidas (toggle)
  const applyMarkup15 = () => {
    setValoracionDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      const allAt15 = (next.partidas || []).length > 0 && next.partidas.every((p) => _toNum(p.mercado_pct, 0) === 15);
      const newPct = allAt15 ? 0 : 15;
      next.partidas = (next.partidas || []).map((p) => ({ ...p, mercado_pct: newPct }));
      const note = "+15 % precio de mercado aplicado a todas las partidas.";
      if (newPct === 15) {
        next.observaciones = next.observaciones
          ? (next.observaciones.includes(note) ? next.observaciones : `${next.observaciones} ${note}`)
          : note;
      } else if (next.observaciones) {
        next.observaciones = next.observaciones.replace(note, "").trim() || null;
      }
      return recalcTotals(next);
    });
    toast.success("+15 % de mercado actualizado en todas las partidas");
  };

  // +21 % IVA en TODAS las partidas
  const applyIVA21 = () => {
    setValoracionDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.partidas = (next.partidas || []).map((p) => ({ ...p, iva_pct: 21 }));
      return recalcTotals(next);
    });
    toast.success("+21 % IVA aplicado a todas las partidas");
  };

  const removeIVA21 = () => {
    setValoracionDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.partidas = (next.partidas || []).map((p) => ({ ...p, iva_pct: 0 }));
      return recalcTotals(next);
    });
  };

  const valoracionDirty = JSON.stringify(valoracion) !== JSON.stringify(valoracionDraft);

  // Parseo numérico tolerante con notación española: acepta tanto coma como
  // punto como separador decimal y devuelve number (0 si vacío/inválido).
  // Detecta los siguientes formatos:
  //   "85,5"        → 85.5
  //   "85.5"        → 85.5
  //   "100020,20"   → 100020.20
  //   "100.020,20"  → 100020.20  (notación española: punto de miles + coma decimal)
  //   "100,020.20"  → 100020.20  (notación anglosajona)
  // Necesario porque algunos peritos tipean con coma o con notación con miles.
  const parseNumLocale = (v) => {
    if (v === "" || v == null) return 0;
    let s = String(v).replace(/[€\s]/g, "").trim();
    if (!s) return 0;
    const hasDot = s.includes(".");
    const hasComma = s.includes(",");
    if (hasDot && hasComma) {
      // El separador más a la derecha marca los decimales; el otro son miles.
      if (s.lastIndexOf(",") > s.lastIndexOf(".")) {
        s = s.replace(/\./g, "").replace(",", ".");
      } else {
        s = s.replace(/,/g, "");
      }
    } else if (hasComma) {
      s = s.replace(",", ".");
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  // Formateo de euros con notación española: punto de miles, coma decimal,
  // 2 decimales exactos. Ej: 100020.2 → "100.020,20 €". Centralizado para que
  // toda la valoración (tabla, totales, copia al portapapeles, IVA) use el
  // mismo formato y el perito vea siempre 433,86 € en lugar de 433.86 €.
  const formatEur = (n) =>
    Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

  // === Copiar al portapapeles ===
  const copyText = async (text, label = "Copiado") => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(label);
    } catch (e) { toast.error("No se pudo copiar"); }
  };

  const buildValoracionPlainText = () => {
    const v = valoracionDraft || valoracion;
    if (!v) return "";
    const lines = [];
    const header = policyHeader();
    if (header) {
      lines.push(header);
      lines.push("");
    }
    if ((v.partidas || []).length) {
      lines.push("Partidas:");
      lines.push("");
      v.partidas.forEach((p, idx) => {
        const code = (p.baremo_code || (p.sin_baremo ? "Sin baremo" : "—")).toString();
        const partida = (p.baremo_descripcion || "").toString().trim();
        const descLibre = (p.concepto || "").toString().trim();
        const tipo = p.tipo === "contenido" ? "Contenido" : "Continente";
        const uds = Number(p.unidades || 0);
        const eur_u = Number(p.precio_unidad_eur || 0);
        const dep = Number(p.depreciacion_pct || 0);
        const mkt = Number(p.mercado_pct || 0);
        const iva = Number(p.iva_pct || 0);
        const tot = Number(p.total_partida_eur || 0);
        // Bloque por partida: incluye TANTO la partida del baremo como la
        // descripción libre del perito. Más legible al pegarlo en ERP/email
        // que una tabla ASCII de 150 caracteres de ancho.
        lines.push(`${idx + 1}) [${code}] ${partida || "(sin partida)"}`);
        if (descLibre && descLibre !== partida) {
          lines.push(`   Descripción libre: ${descLibre}`);
        }
        lines.push(`   Tipo: ${tipo} · Uds: ${uds} · €/u: ${formatEur(eur_u)} · %Dep: ${dep} · %Mkt: ${mkt} · %IVA: ${iva}`);
        lines.push(`   Total partida: ${formatEur(tot)}`);
        lines.push("");
      });
    }
    if (v.subtotal_sin_iva != null && v.iva_aplicado) {
      lines.push(`Subtotal sin IVA: ${formatEur(v.subtotal_sin_iva)}`);
      lines.push(`IVA (${v.iva_pct_global || 21} %): ${formatEur(v.iva_eur || 0)}`);
      lines.push(`TOTAL con IVA: ${formatEur(v.total_estimado_eur || 0)}`);
    } else if (v.total_estimado_eur != null) {
      lines.push(`TOTAL: ${formatEur(v.total_estimado_eur)}`);
    }
    // Sublímites y franquicia aplicados por la póliza (si la IA los detectó).
    const limites = v.limites_aplicados || [];
    const franq = v.franquicia_eur;
    const totalNeto = v.total_neto_eur;
    if (limites.length > 0 || (franq != null && franq > 0) || (totalNeto != null && totalNeto !== v.total_estimado_eur)) {
      lines.push("");
      lines.push("SUBLÍMITES Y FRANQUICIA APLICADOS POR LA PÓLIZA");
      lines.push("-----------------------------------------------");
      limites.forEach((lim, k) => {
        lines.push(`${k + 1}) ${lim.garantia} — límite ${formatEur(lim.limite_eur)} (${lim.base})`);
        lines.push(`   Bruto partidas: ${formatEur(lim.bruto_eur)} → aplicado: ${formatEur(lim.importe_aplicado)}`);
        if (lim.cita) lines.push(`   «${lim.cita}»`);
      });
      if (franq != null && franq > 0) lines.push(`Franquicia: − ${formatEur(franq)}`);
      if (totalNeto != null) lines.push(`TOTAL NETO A INDEMNIZAR: ${formatEur(totalNeto)}`);
    }
    // Si hay preexistencia calculada, añadimos el bloque de capitales recomendados
    // e infraseguro para que se vuelquen junto a la valoración al portapapeles.
    const pr = preex?.result;
    if (pr) {
      const fmt = (n) => n == null
        ? "—"
        : Number(n).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
      const pct = (n) => n == null
        ? "—"
        : Number(n).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " %";
      lines.push("");
      lines.push("PREEXISTENCIA E INFRASEGURO");
      lines.push("---------------------------");
      lines.push(`Capital de continente recomendado: ${fmt(pr.preexistencia_continente)}`);
      lines.push(`Capital de contenido recomendado:  ${fmt(pr.preexistencia_contenido)}`);
      if (pr.infraseguro_continente_pct != null) {
        lines.push(`Infraseguro continente: ${pct(pr.infraseguro_continente_pct)}`);
      }
      if (pr.infraseguro_contenido_pct != null) {
        lines.push(`Infraseguro contenido:  ${pct(pr.infraseguro_contenido_pct)}`);
      }
      if (pr.indemnizacion_eur != null) {
        lines.push(`Indemnización (regla proporcional): ${fmt(pr.indemnizacion_eur)} (factor ${pct(pr.factor_proporcional_pct)})`);
      }
    }
    // Las observaciones NO se copian — solo la tabla de valoración y los totales.
    return lines.join("\n");
  };

  if (loading || !data) return <div className="text-[#7A786F]">Cargando…</div>;

  const s = data.siniestro;
  const photos = data.photos || [];
  const transcript = data.transcript || [];
  const chat = data.chat || [];
  const documents = data.documents || [];
  const isTelematico = data?.siniestro?.kind === "telematico";

  // Subir documento adicional al expediente
  const onUploadDoc = async (files) => {
    if (!files?.length) return;
    setDocUploadingTel(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/siniestros/${id}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} documento(s) subido(s)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir");
    } finally {
      setDocUploadingTel(false);
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/siniestros/${id}/documents/${docId}`);
      load();
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const toggleDocPoliza = async (d) => {
    const newVal = !d.is_poliza;
    try {
      await api.patch(`/siniestros/${id}/documents/${d.id}`, { is_poliza: newVal });
      setData((prev) => prev ? ({
        ...prev,
        documents: (prev.documents || []).map((x) => x.id === d.id ? { ...x, is_poliza: newVal } : x),
      }) : prev);
      toast.success(newVal ? "Marcado como póliza · la IA lo consultará" : "Marca de póliza retirada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar el documento");
    }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/s/${s.session_token}`;
    await navigator.clipboard.writeText(url);
    toast.success("Enlace copiado al portapapeles");
  };

  // === Gestión de fotos: seleccionar / eliminar / descargar ===
  const togglePhoto = (pid) => {
    setSelectedPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const selectAll = () => setSelectedPhotos(new Set(photos.map((p) => p.id)));
  const clearSelection = () => setSelectedPhotos(new Set());

  const downloadOne = (p) => {
    const url = `${API}/photos/${p.id}?download=1`;
    const a = document.createElement("a");
    a.href = url; a.download = p.filename || `foto_${p.id.slice(0, 8)}.jpg`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const downloadSelectedZip = async () => {
    setPhotoLoading(true);
    try {
      const ids = Array.from(selectedPhotos);
      const isAll = ids.length === 0 || ids.length === photos.length;
      const r = await api.post(`/siniestros/${id}/photos/download-zip`,
        isAll ? {} : { ids }, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `fotos_${s.expediente_num || "expediente"}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Descargadas ${isAll ? photos.length : ids.length} fotos`);
    } catch (e) {
      toast.error("No se pudo descargar el ZIP");
    } finally { setPhotoLoading(false); }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedPhotos);
    if (!ids.length) return;
    if (!window.confirm(`¿Eliminar ${ids.length} foto${ids.length === 1 ? "" : "s"} del expediente?\n\nEsta acción no se puede deshacer. Si tras eliminar quieres un certificado actualizado, vuelve a descargar el PDF — se regenera automáticamente con las fotos restantes.`)) return;
    setPhotoLoading(true);
    try {
      await api.post(`/siniestros/${id}/photos/bulk-delete`, { ids });
      toast.success(`${ids.length} foto${ids.length === 1 ? "" : "s"} eliminada${ids.length === 1 ? "" : "s"}. El certificado se regenerará en la próxima descarga.`);
      clearSelection();
      await load();
    } catch (e) {
      toast.error("No se pudieron eliminar las fotos");
    } finally { setPhotoLoading(false); }
  };

  const deleteOne = async (p) => {
    if (!window.confirm(`¿Eliminar esta foto del expediente?\n\nEl certificado se regenerará al volver a descargarlo.`)) return;
    setPhotoLoading(true);
    try {
      await api.delete(`/photos/${p.id}`);
      toast.success("Foto eliminada");
      setSelectedPhotos((prev) => { const n = new Set(prev); n.delete(p.id); return n; });
      await load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    } finally { setPhotoLoading(false); }
  };
  const resend = async () => {
    try {
      const r = await api.post(`/siniestros/${id}/resend-invite`, { base_url: window.location.origin });
      setData((d) => ({ ...d, siniestro: r.data }));
      if (r.data.invite_sent) toast.success("Enlace reenviado correctamente");
      else toast.error(`Error: ${r.data.invite_error}`);
    } catch (e) {
      toast.error("Error al reenviar");
    }
  };
  const cancel = async () => {
    if (!window.confirm("¿Cancelar este siniestro?")) return;
    await api.delete(`/siniestros/${id}`);
    toast.success("Siniestro cancelado");
    nav("/");
  };
  const finalize = async () => {
    if (!window.confirm("¿Finalizar el siniestro y generar certificado?")) return;
    try {
      await api.post(`/siniestros/${id}/finalize`);
      toast.success("Sesión finalizada · certificado generado");
      await load();
      window.open(`${API}/siniestros/${id}/certificate?token=${token()}&mode=completo`, "_blank");
    } catch (e) {
      toast.error("Error al finalizar");
    }
  };
  const downloadCert = (mode = "completo") => {
    // mode: "completo" (default) | "simplificado"
    window.open(`${API}/siniestros/${id}/certificate?token=${token()}&mode=${mode}`, "_blank");
  };

  const statusLabel = {
    pendiente: "Pendiente",
    en_curso: "En curso",
    finalizado: "Finalizada",
    cancelado: "Cancelada",
  }[s.status] || s.status;

  return (
    <div className="space-y-6" data-testid="siniestro-detail-root">
      <Link to="/" className="text-sm text-[#7A786F] hover:text-[#2C2C2A] inline-flex items-center">
        <ArrowLeft className="h-4 w-4 mr-1" /> Volver al panel
      </Link>

      <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#7A786F]">Expediente</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-[#2C2C2A]">{s.expediente_num}</h1>
            <div className="text-[#7A786F] mt-1">
              {s.asegurado_nombre}
              {!isTelematico && s.asegurado_telefono && (
                <> · <span className="font-mono text-[#2C2C2A]">{s.asegurado_telefono}</span></>
              )}
            </div>
            {s.compania_name && (
              <div className="text-[12px] text-[#7A786F] mt-1" data-testid="siniestro-compania">
                Compañía: <span className="text-[#2C2C2A] font-medium">{s.compania_name}</span>
                {s.policy_template_name && s.policy_template_name !== "Otra póliza" && (
                  <span data-testid="siniestro-policy-template">
                    {" · "}Modalidad de póliza suscrita: <span className="text-[#2C2C2A] font-semibold uppercase tracking-wide">{s.policy_template_name}</span>
                  </span>
                )}
                {s.policy_template_name === "Otra póliza" && (
                  <span data-testid="siniestro-policy-otra">{" · "}Modalidad: <em>otra póliza</em> (criterios genéricos)</span>
                )}
              </div>
            )}
            <div className="mt-3 flex gap-2 items-center flex-wrap">
              <Badge variant="outline" className="bg-[#FAECE7] text-[#A14620] border-transparent rounded-full">
                {statusLabel}
              </Badge>
              {isTelematico && (
                <Badge variant="outline" className="bg-[#EAF1FB] text-[#2E5AAC] border-transparent rounded-full" data-testid="kind-badge-telematico">
                  Expediente telemático
                </Badge>
              )}
              {!isTelematico && s.invite_sent && (
                <Badge variant="outline" className="bg-[#E6F5EF] text-[#1D9E75] border-transparent rounded-full" data-testid="invite-sent-badge">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Enviado por {s.canal === "whatsapp" ? "WhatsApp" : "SMS"}
                </Badge>
              )}
              {!isTelematico && s.invite_status === "delivered" && (
                <Badge variant="outline" className="bg-[#E6F5EF] text-[#1D9E75] border-transparent rounded-full" data-testid="invite-delivered-badge" title={s.invite_status_text || "Entregado al móvil"}>
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Entregado al móvil
                </Badge>
              )}
              {!isTelematico && s.invite_status === "failed" && s.invite_sent && (
                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-transparent rounded-full" data-testid="invite-failed-badge" title={s.invite_status_text || "No entregado"}>
                  No entregado: {s.invite_status_text || "—"}
                </Badge>
              )}
              {!isTelematico && s.invite_status === "pending" && s.invite_sent && (
                <Badge variant="outline" className="bg-amber-50 text-amber-800 border-transparent rounded-full" data-testid="invite-pending-badge" title={s.invite_status_text}>
                  En reparto: {s.invite_status_text || "esperando red"}
                </Badge>
              )}
              {!isTelematico && s.consent_at && (
                <Badge variant="outline" className="bg-[#FBFAF6] text-[#2C2C2A] border-[#E5E2D8] rounded-full">
                  RGPD aceptado
                </Badge>
              )}
              {s.erp_synced_at && (
                <Badge variant="outline" className="bg-[#FBFAF6] text-[#2C2C2A] border-[#E5E2D8] rounded-full">
                  ERP sincronizado
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isTelematico && s.status !== "finalizado" && s.status !== "cancelado" && (
              <Button onClick={() => nav(`/call/${id}`)} className="btn-eks-primary rounded-[12px]" data-testid="enter-call-btn">
                <Video className="h-4 w-4 mr-1.5" /> Entrar a la sala
              </Button>
            )}
            {!isTelematico && (
              <Button variant="outline" onClick={copyLink} className="border-[#E5E2D8] rounded-[12px] hover:bg-[#FAECE7]" data-testid="copy-link-btn">
                <Copy className="h-4 w-4 mr-1.5" /> Copiar enlace
              </Button>
            )}
            {!isTelematico && s.status !== "finalizado" && s.status !== "cancelado" && (
              <Button variant="outline" onClick={resend} className="border-[#E5E2D8] rounded-[12px] hover:bg-[#FAECE7]" data-testid="resend-btn">
                <RefreshCcw className="h-4 w-4 mr-1.5" /> Reenviar
              </Button>
            )}
            {s.status === "finalizado" ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-[#E5E2D8] rounded-[12px] hover:bg-[#FAECE7]"
                    data-testid="download-cert-btn"
                  >
                    <FileDown className="h-4 w-4 mr-1.5" /> Descargar certificado
                    <ChevronDown className="h-4 w-4 ml-1.5 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Elige el formato del PDF</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => downloadCert("completo")}
                    className="flex-col items-start gap-0.5 py-2.5"
                    data-testid="download-cert-completo"
                  >
                    <span className="font-medium">Completo</span>
                    <span className="text-xs text-[#7A786F] leading-tight">
                      Informe pericial · narrativa, valoración, preexistencia, fotos y QR
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => downloadCert("simplificado")}
                    className="flex-col items-start gap-0.5 py-2.5"
                    data-testid="download-cert-simplificado"
                  >
                    <span className="font-medium">Simplificado</span>
                    <span className="text-xs text-[#7A786F] leading-tight">
                      Solo datos, geolocalización, fotos y QR de validación
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="outline" onClick={finalize} className="border-[#E5E2D8] rounded-[12px] hover:bg-[#FAECE7]" data-testid="finalize-btn">
                <FileDown className="h-4 w-4 mr-1.5" /> Finalizar y certificar
              </Button>
            )}
            {s.status === "finalizado" && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const r = await api.post(`/siniestros/${id}/resend-to-erp`);
                    if (r.data?.ok) {
                      toast.success(`Reenviado al ERP: ${r.data.photos_ok}/${r.data.photos_resent} fotos + certificado`);
                    } else {
                      toast.error(`No se pudo reenviar: ${r.data?.finalized?.error || "error desconocido"}`);
                    }
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "No se pudo reenviar al ERP");
                  }
                }}
                className="border-[#E5E2D8] rounded-[12px] hover:bg-[#FAECE7]"
                data-testid="resend-to-erp-btn"
                title="Reenviar fotos + certificado al ERP (Ekspertium/Avizor)"
              >
                <RefreshCcw className="h-4 w-4 mr-1.5" /> Reenviar al ERP
              </Button>
            )}
            {s.status !== "finalizado" && s.status !== "cancelado" && (
              <Button variant="outline" onClick={cancel} className="border-[#E5E2D8] rounded-[12px] text-[#A32D2D] hover:bg-[#FAECE7] hover:text-[#A32D2D]" data-testid="cancel-btn">
                <X className="h-4 w-4 mr-1.5" /> Cancelar
              </Button>
            )}
          </div>
        </div>
        {s.erp_webhook_last && s.status === "finalizado" && (() => {
          const w = s.erp_webhook_last;
          const respBody = (w.response_body || "").toLowerCase();
          const skipped = respBody.includes("skipped") || respBody.includes("not_found") || respBody.includes("not found");
          const isWarn = w.ok && skipped;
          const isError = !w.ok;
          const isOk = w.ok && !skipped;
          return (
            <div
              className={`mt-3 rounded-[10px] border px-3 py-2 text-xs flex items-start gap-2 ${
                isOk
                  ? "bg-[#E4F5EE] border-[#B7DFCA] text-[#1D6D57]"
                  : isWarn
                  ? "bg-[#FDF1EC] border-[#F2C7B4] text-[#A14620]"
                  : "bg-[#FBEEEA] border-[#F2D0C6] text-[#B43F3F]"
              }`}
              data-testid="erp-sync-status"
            >
              {isOk ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium">
                  {isOk && "Sincronizado con ERP"}
                  {isWarn && "El ERP recibió el evento pero lo descartó"}
                  {isError && "Fallo al sincronizar con ERP"}
                  {" "}<span className="opacity-70">· {new Date(w.at).toLocaleString("es-ES")}</span>
                </div>
                {isError && (
                  <div className="mt-0.5 opacity-90 break-all">
                    {w.error || "Sin detalle del error"}
                    {w.attempts && ` · ${w.attempts} intentos`}
                  </div>
                )}
                {isWarn && (
                  <div className="mt-1 opacity-90 break-all">
                    <div>Respuesta de Ekspertium: <code className="bg-white/60 px-1 rounded">{w.response_body}</code></div>
                    <div className="mt-1">Ekspertium NO encuentra el expediente <code className="bg-white/60 px-1 rounded">{s.expediente_num}</code> en su base de datos. Verifica que el expediente esté creado en Ekspertium con ese mismo número, y que ambas apps apunten al mismo entorno (preview / producción).</div>
                  </div>
                )}
                {w.response_body && isOk && (
                  <div className="mt-0.5 opacity-80 break-all text-[11px]">
                    Respuesta: <code className="bg-white/60 px-1 rounded">{w.response_body?.slice(0, 200)}</code>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        {s.descripcion && (
          <div className="mt-5 text-sm text-[#2C2C2A] leading-relaxed bg-[#FBFAF6] border border-[#E5E2D8] rounded-[12px] p-4 whitespace-pre-wrap">
            {s.descripcion}
          </div>
        )}
      </div>

      <Tabs defaultValue="photos">
        <TabsList className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[12px] p-1">
          <TabsTrigger value="photos" className="rounded-[10px] data-[state=active]:bg-white data-[state=active]:text-[#D85A30] data-[state=active]:shadow-none">
            <ImagePlus className="h-3.5 w-3.5 mr-1.5" /> Fotos ({photos.length})
          </TabsTrigger>
          <TabsTrigger value="transcript" className="rounded-[10px] data-[state=active]:bg-white data-[state=active]:text-[#D85A30] data-[state=active]:shadow-none">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Transcripción ({transcript.length})
          </TabsTrigger>
          <TabsTrigger value="chat" className="rounded-[10px] data-[state=active]:bg-white data-[state=active]:text-[#D85A30] data-[state=active]:shadow-none">
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Chat ({chat.length})
          </TabsTrigger>
          <TabsTrigger value="documents" className="rounded-[10px] data-[state=active]:bg-white data-[state=active]:text-[#D85A30] data-[state=active]:shadow-none" data-testid="tab-documents">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Documentos ({documents.length})
          </TabsTrigger>
          <TabsTrigger value="ai" className="rounded-[10px] data-[state=active]:bg-white data-[state=active]:text-[#D85A30] data-[state=active]:shadow-none" data-testid="tab-ai-notes">
            <Brain className="h-3.5 w-3.5 mr-1.5" /> Notas IA
          </TabsTrigger>
        </TabsList>

        <TabsContent value="photos" className="mt-4">
          {photos.length === 0 ? (
            <div className="text-sm text-[#7A786F] py-10 text-center bg-white border border-dashed border-[#E5E2D8] rounded-[14px]">
              Aún no hay fotografías capturadas.
            </div>
          ) : (
            <>
              {/* Toolbar de selección */}
              <div className="flex flex-wrap items-center gap-2 mb-3 bg-white border border-[#E5E2D8] rounded-[12px] p-2.5">
                <span className="text-sm text-[#2C2C2A] font-medium px-1" data-testid="photos-selection-count">
                  {selectedPhotos.size === 0
                    ? `${photos.length} foto${photos.length === 1 ? "" : "s"}`
                    : `${selectedPhotos.size} de ${photos.length} seleccionada${selectedPhotos.size === 1 ? "" : "s"}`}
                </span>
                <div className="ml-auto flex flex-wrap gap-2">
                  {selectedPhotos.size === 0 ? (
                    <Button size="sm" variant="outline" className="border-[#E5E2D8] rounded-[10px]" onClick={selectAll} data-testid="select-all-photos-btn">
                      Seleccionar todas
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="border-[#E5E2D8] rounded-[10px]" onClick={clearSelection} data-testid="clear-photo-selection-btn">
                      Quitar selección
                    </Button>
                  )}
                  <Button
                    size="sm" variant="outline"
                    className="border-[#E5E2D8] rounded-[10px] hover:bg-[#FAECE7]"
                    onClick={downloadSelectedZip}
                    disabled={photoLoading}
                    data-testid="download-photos-zip-btn"
                  >
                    <FileDown className="h-3.5 w-3.5 mr-1.5" />
                    Descargar {selectedPhotos.size === 0 ? "todas" : "selección"} (ZIP)
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    className="border-[#E5E2D8] rounded-[10px] text-[#A32D2D] hover:bg-[#FAECE7] hover:text-[#A32D2D]"
                    onClick={deleteSelected}
                    disabled={photoLoading || selectedPhotos.size === 0}
                    data-testid="delete-selected-photos-btn"
                  >
                    <X className="h-3.5 w-3.5 mr-1.5" />
                    Eliminar selección
                  </Button>
                </div>
              </div>

              <RoomSummariesPanel siniestroId={id} refreshKey={photos.length + "|" + photos.map((p) => (p.room || "-") + (p.measurements ? "1" : "0")).join(",")} />
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {photos.map((p) => {
                  const selected = selectedPhotos.has(p.id);
                  return (
                    <div
                      key={p.id}
                      className={`bg-white border rounded-[14px] overflow-hidden transition-all relative group ${selected ? "border-[#D85A30] ring-2 ring-[#D85A30]/30" : "border-[#E5E2D8]"}`}
                      data-testid={`photo-card-${p.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => togglePhoto(p.id)}
                        className={`absolute top-2 left-2 z-10 h-6 w-6 rounded-md flex items-center justify-center border-2 transition-all ${selected ? "bg-[#D85A30] border-[#D85A30] text-white" : "bg-white/90 border-[#7A786F]/40 text-transparent hover:border-[#D85A30]"}`}
                        title={selected ? "Deseleccionar" : "Seleccionar"}
                        data-testid={`photo-check-${p.id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </button>
                      <img
                        src={`${API}/photos/${p.id}`}
                        alt={p.filename}
                        className="w-full h-48 object-cover cursor-pointer"
                        onClick={() => togglePhoto(p.id)}
                      />
                      <div className="p-3 text-xs text-[#2C2C2A] space-y-1">
                        <div className="font-mono text-[#7A786F]">{fmtDateTime(p.captured_at)}</div>
                        {p.lat != null && p.lng != null ? (
                          <div className="inline-flex items-center text-[#1D9E75]">
                            <MapPin className="h-3 w-3 mr-1" /> {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                          </div>
                        ) : (
                          <div className="text-[#7A786F]">Sin geolocalización</div>
                        )}
                        <PhotoDescription
                          photo={p}
                          onSaved={load}
                        />
                        <PhotoRoomSelector
                          photo={p}
                          catalog={roomCatalog}
                          onSaved={load}
                        />
                        {p.measurements && (p.measurements.mediciones || []).length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setMeasurePhoto(p); }}
                            className="w-full text-left bg-[#FAECE7] border border-[#F2D0C6] rounded-[8px] px-2 py-1.5 hover:border-[#D85A30] transition-colors"
                            title="Ver detalle de la medición IA"
                            data-testid={`photo-measurement-inline-${p.id}`}
                          >
                            <div className="flex items-baseline gap-1.5 flex-wrap">
                              <Ruler className="h-3 w-3 text-[#D85A30] shrink-0 relative top-[1px]" />
                              <span className="text-[10px] uppercase tracking-wider text-[#7A2A14] font-semibold">
                                {(p.measurements.mediciones[0].objetivo || "Superficie").slice(0, 32)}
                              </span>
                            </div>
                            <div className="mt-0.5 flex items-baseline gap-1.5 flex-wrap">
                              <span className="text-base font-bold text-[#D85A30]">
                                {typeof p.measurements.mediciones[0].valor_principal === "number"
                                  ? p.measurements.mediciones[0].valor_principal.toLocaleString("es-ES", { maximumFractionDigits: 2 })
                                  : (p.measurements.mediciones[0].valor_principal ?? "—")}
                              </span>
                              <span className="text-[11px] text-[#7A2A14]">{p.measurements.mediciones[0].unidad || ""}</span>
                              {p.measurements.mediciones[0].incertidumbre_pct != null && (
                                <span className="text-[10px] text-[#B98A18] font-medium">
                                  ±{p.measurements.mediciones[0].incertidumbre_pct}%
                                </span>
                              )}
                            </div>
                          </button>
                        )}
                        <div className="flex gap-1 pt-1.5 opacity-90">
                          <button
                            onClick={(e) => { e.stopPropagation(); setMeasurePhoto(p); }}
                            className={`flex-1 text-[11px] py-1 rounded-md border flex items-center justify-center gap-1 ${p.measurements ? "border-[#D85A30] bg-[#FAECE7] text-[#7A2A14]" : "border-[#E5E2D8] hover:bg-[#FAECE7]"}`}
                            title={p.measurements ? "Ver / editar medición IA" : "Medir esta foto con IA"}
                            data-testid={`measure-photo-btn-${p.id}`}
                          >
                            <Ruler className="h-3 w-3" />
                            {p.measurements ? "Medida IA" : "Medir"}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); downloadOne(p); }}
                            className="flex-1 text-[11px] py-1 rounded-md border border-[#E5E2D8] hover:bg-[#FAECE7] flex items-center justify-center gap-1"
                            data-testid={`download-photo-${p.id}`}
                          >
                            <FileDown className="h-3 w-3" /> Descargar
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteOne(p); }}
                            className="text-[11px] py-1 px-2 rounded-md border border-[#E5E2D8] hover:bg-[#FAECE7] text-[#A32D2D]"
                            title="Eliminar foto"
                            data-testid={`delete-photo-${p.id}`}
                            disabled={photoLoading}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {measurePhoto && (
                <PhotoMeasureDialog
                  photo={measurePhoto}
                  open={!!measurePhoto}
                  onOpenChange={(v) => { if (!v) setMeasurePhoto(null); }}
                  onSaved={load}
                />
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="transcript" className="mt-4">
          <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6 space-y-3">
            <div className="text-xs text-[#7A786F]">
              La transcripción se guarda solo en el expediente. No aparece en el PDF del certificado.
            </div>
            {transcript.length === 0 ? (
              <div className="text-sm text-[#7A786F] text-center py-8">Sin transcripción todavía.</div>
            ) : transcript.map((t) => (
              <div key={t.id} className="text-sm flex gap-3 items-start">
                <span className="text-[11px] text-[#7A786F] font-mono shrink-0 w-20 pt-0.5">
                  {fmtTime(t.created_at)}
                </span>
                <Badge
                  variant="outline"
                  className={`h-6 capitalize text-[11px] rounded-full shrink-0 ${t.role === "perito" ? "bg-[#FAECE7] text-[#D85A30] border-transparent" : "bg-[#E6F5EF] text-[#1D9E75] border-transparent"}`}
                >
                  {t.role}
                </Badge>
                <span className="text-[#2C2C2A]">{t.text}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="chat" className="mt-4">
          <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6 space-y-2">
            {chat.length === 0 ? (
              <div className="text-sm text-[#7A786F] text-center py-8">Sin mensajes de chat.</div>
            ) : chat.map((m) => (
              <div key={m.id} className="text-sm">
                <span className="text-[11px] text-[#7A786F] font-mono mr-2">
                  {fmtTime(m.created_at)}
                </span>
                <strong className={`capitalize mr-1 ${m.role === "perito" ? "text-[#D85A30]" : "text-[#1D9E75]"}`}>{m.role}:</strong>
                <span className="text-[#2C2C2A]">{m.text}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6" data-testid="documents-block">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <FileText className="h-5 w-5 text-[#D85A30]" />
              <div className="font-medium text-[#2C2C2A]">Documentos del expediente</div>
            </div>
            <DropZone
              label={docUploadingTel ? "Subiendo…" : "Arrastra documentos o haz click para seleccionar"}
              hint="PDF, DOC, DOCX, TXT, XLS, XLSX · puedes soltar varios a la vez"
              accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,application/pdf"
              busy={docUploadingTel}
              onFile={(f) => onUploadDoc([f])}
              multiple
              testid="docs-dropzone"
            />
            <div className="mt-3">
            {documents.length === 0 ? (
              <div className="text-sm text-[#7A786F] py-6 text-center">
                Aún no hay documentos. Sube partes, presupuestos, pólizas u otra documentación relevante.
              </div>
            ) : (
              <div className="space-y-1.5">
                {documents.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-2 text-sm" data-testid={`doc-row-${d.id}`}>
                    <FileText className="h-4 w-4 text-[#7A786F]" />
                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-[#2C2C2A] hover:text-[#D85A30] hover:underline">
                      {d.filename}
                    </a>
                    {d.is_poliza && (
                      <span className="text-[10px] uppercase tracking-wider text-[#2E5AAC] bg-[#EAF1FB] rounded-full px-2 py-0.5 font-semibold" data-testid={`doc-poliza-badge-${d.id}`}>Póliza · IA</span>
                    )}
                    <span className="text-[11px] text-[#7A786F] font-mono">{Math.round(d.size_bytes / 1024)} KB</span>
                    <button
                      onClick={() => toggleDocPoliza(d)}
                      className={`text-[11px] rounded-[8px] px-2 py-1 border ${d.is_poliza ? "border-[#2E5AAC] text-[#2E5AAC] bg-[#EAF1FB]" : "border-[#E5E2D8] text-[#7A786F] hover:text-[#2E5AAC] hover:border-[#2E5AAC]"}`}
                      title={d.is_poliza ? "Quitar marca de póliza" : "Marcar como póliza para que la IA lo consulte"}
                      data-testid={`doc-poliza-toggle-${d.id}`}
                    >
                      {d.is_poliza ? "Es póliza ✓" : "Marcar póliza"}
                    </button>
                    <button onClick={() => deleteDoc(d.id)} className="text-[#7A786F] hover:text-[#B43F3F] p-1" title="Eliminar"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>
        </TabsContent>


        <TabsContent value="ai" className="mt-4 space-y-4">
          <AiInstructionsPanel
            causasDraft={aiInstrCausasDraft}
            onCausasDraftChange={setAiInstrCausasDraft}
            causasMeta={aiInstrCausas}
            causasRecording={aiInstrCausasRecording}
            causasTranscribing={aiInstrCausasTranscribing}
            onCausasStartRecording={_instrCausasRec.start}
            onCausasStopRecording={_instrCausasRec.stop}
            valoracionDraft={aiInstrValoracionDraft}
            onValoracionDraftChange={setAiInstrValoracionDraft}
            valoracionMeta={aiInstrValoracion}
            valoracionRecording={aiInstrValoracionRecording}
            valoracionTranscribing={aiInstrValoracionTranscribing}
            onValoracionStartRecording={_instrValoracionRec.start}
            onValoracionStopRecording={_instrValoracionRec.stop}
            saving={aiInstructionsSaving}
            onSave={guardarAiInstructions}
          />

          {/* Notas estructuradas */}
          <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6" data-testid="ai-notes-block">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="h-4 w-4 text-[#D85A30]" />
              <div className="font-medium text-[#2C2C2A]">Datos extraídos por IA · riesgos diversos</div>
              {aiUpdatedAt && (
                <span className="text-[11px] text-[#7A786F] font-mono">
                  act. {new Date(aiUpdatedAt).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}
                </span>
              )}
              <Button size="sm" variant="outline" onClick={refreshAiNotes} disabled={aiLoading} className="ml-auto" data-testid="ai-notes-refresh-btn">
                {aiLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCcw className="h-3 w-3 mr-1" />}
                Recalcular IA
              </Button>
            </div>
            {!aiNotes || !AI_NOTES_LABELS.some(([k]) => aiNotes[k]) ? (
              <div className="text-sm text-[#7A786F] py-6 text-center">
                Aún no hay datos extraídos.
                {(transcript.length === 0)
                  ? " Necesitas que haya transcripción para que la IA pueda procesar."
                  : " Pulsa \"Recalcular IA\" para procesar la transcripción actual."}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {AI_NOTES_LABELS.map(([k, label]) => {
                  const v = aiNotes[k];
                  if (!v) return null;
                  return (
                    <div key={k} className="flex gap-3" data-testid={`ai-note-${k}`}>
                      <span className="text-[#7A786F] shrink-0 w-[140px]">{label}</span>
                      <span className="text-[#2C2C2A] break-words">{v}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="text-[11px] text-[#7A786F] mt-3 italic">
              Transcripción analizada: {aiTranscriptChars} caracteres.
            </div>
          </div>

          {/* Narrativa para Ekspertium */}
          <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6" data-testid="ai-narrative-block">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-[#D85A30]" />
              <div className="font-medium text-[#2C2C2A]">Causas y circunstancias · texto para Ekspertium</div>
              {aiNarrativeEdited && (
                <Badge variant="outline" className="bg-[#E6F5EF] text-[#1D9E75] border-transparent rounded-full text-[10px]">·editado</Badge>
              )}
            </div>
            <div className="text-xs text-[#7A786F] mb-3">
              Este texto se enviará al ERP en el campo &quot;Causas y circunstancias&quot; del informe al pulsar Finalizar. Edítalo libremente.
            </div>
            <textarea
              value={narrativeDraft}
              onChange={(e) => setNarrativeDraft(e.target.value)}
              className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm leading-relaxed p-3 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none resize-y font-mono"
              rows={10}
              placeholder="El texto narrativo aparecerá aquí cuando haya transcripción suficiente."
              data-testid="ai-narrative-textarea"
            />
            <div className="flex items-center gap-2 justify-end mt-3">
              <Button variant="outline" size="sm" onClick={regenerateNarrative} disabled={aiLoading} data-testid="ai-narrative-regenerate-btn">
                Regenerar desde IA
              </Button>
              <Button variant="outline" size="sm" onClick={copyNarrative} disabled={!aiNarrative} data-testid="ai-narrative-copy-btn">
                <Copy className="h-3 w-3 mr-1" /> Copiar
              </Button>
              <Button size="sm" onClick={saveNarrative} disabled={aiLoading || narrativeDraft === aiNarrative} className="bg-[#D85A30] hover:bg-[#C04D27] text-white" data-testid="ai-narrative-save-btn">
                {aiLoading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>

          {/* Valoración sugerida (Phase 2) */}
          <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6" data-testid="ai-valoracion-block">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Sparkles className="h-4 w-4 text-[#D85A30]" />
              <div className="font-medium text-[#2C2C2A]">Valoración sugerida · análisis IA</div>
              {valoracionEdited && (
                <Badge variant="outline" className="bg-[#E6F5EF] text-[#1D9E75] border-transparent rounded-full text-[10px]">·editado</Badge>
              )}
              {valoracion?.calidad_evaluacion && (
                <Badge variant="outline" className="rounded-full text-[10px] border-[#E5E2D8] text-[#7A786F]">
                  Calidad: {valoracion.calidad_evaluacion}
                </Badge>
              )}
              {valoracionUpdatedAt && (
                <span className="text-[11px] text-[#7A786F] font-mono">
                  act. {new Date(valoracionUpdatedAt).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}
                </span>
              )}
              {photos.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={generateValoracion}
                  disabled={valoracionLoading}
                  className="ml-auto"
                  data-testid="ai-valoracion-generate-btn"
                  title="Combina fotos + narrativa + notas + transcripción para generar la valoración"
                >
                  {valoracionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                  {valoracion ? "Recalcular con fotos" : "Generar con fotos"}
                </Button>
              )}
            </div>

            {photos.length === 0 && (
              <div className="text-xs text-[#7A786F] bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-3 mb-3 flex items-start gap-3" data-testid="ai-valoracion-no-photos">
                <span className="leading-snug flex-1">
                  No hay fotografías en este siniestro. Puedes generar una valoración solo con el texto del informe IA (causas + notas + transcripción), pero será orientativa y sin verificación visual.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={generateValoracion}
                  disabled={valoracionLoading || (!aiNarrative && !aiNotes)}
                  className="shrink-0"
                  data-testid="ai-valoracion-generate-textonly-btn"
                  title="Genera la valoración usando solo el texto del informe IA"
                >
                  {valoracionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                  {valoracion ? "Recalcular solo texto" : "Generar solo con texto"}
                </Button>
              </div>
            )}

            {valoracionDraft?._error && (
              <div className="text-xs text-[#B43F3F] bg-[#FBEEEA] border border-[#F5C2B3] rounded-[10px] p-3 mb-3" data-testid="ai-valoracion-error">
                IA: {valoracionDraft._error}
              </div>
            )}

            {!valoracionDraft && !valoracionLoading && photos.length > 0 && (
              <div className="text-sm text-[#7A786F] py-6 text-center">
                Aún no se ha generado una valoración. Pulsa <strong>&quot;Generar con fotos&quot;</strong> para que la IA combine las {photos.length} foto{photos.length === 1 ? "" : "s"} del siniestro con la narrativa y las notas.
              </div>
            )}

            {valoracionDraft && (
              <div className="space-y-4">
                {/* Tabla de partidas */}
                <div>
                  <div className="text-xs font-medium text-[#7A786F] uppercase tracking-wide mb-2">Partidas</div>
                  {(valoracionDraft.partidas || []).length === 0 ? (
                    <div className="text-sm text-[#7A786F] italic">Sin partidas. Añade una manualmente o vuelve a generar con IA.</div>
                  ) : (
                    <div className="border border-[#E5E2D8] rounded-[10px] overflow-x-auto">
                      <table className="w-full text-sm min-w-[920px]">
                        <thead className="bg-[#FBFAF6] text-[#7A786F] text-[10px] uppercase tracking-wide">
                          <tr>
                            <th className="text-left px-2 py-2 font-medium w-[200px]">Partida (baremo)</th>
                            <th className="text-left px-2 py-2 font-medium">Descripción libre</th>
                            <th className="text-left px-2 py-2 font-medium w-[110px]" title="Continente o contenido">Tipo</th>
                            <th className="text-left px-2 py-2 font-medium w-[120px]" title="Documento de origen">Fuente</th>
                            <th className="text-right px-2 py-2 font-medium w-[64px]">Uds</th>
                            <th className="text-right px-2 py-2 font-medium w-[88px]">€/u</th>
                            <th className="text-right px-2 py-2 font-medium w-[64px]" title="% depreciación">%Dep</th>
                            <th className="text-right px-2 py-2 font-medium w-[64px]" title="% precio mercado">%Mkt</th>
                            <th className="text-right px-2 py-2 font-medium w-[64px]" title="% IVA">%IVA</th>
                            <th className="text-right px-2 py-2 font-medium w-[100px]">Total €</th>
                            <th className="w-[28px]"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(valoracionDraft.partidas || []).map((p, i) => {
                            // Tooltip del row: 3 estados (sin baremo / con código / silencio). Plano para evitar ternario anidado.
                            let rowTitle = "";
                            if (p.sin_baremo) rowTitle = "Sin baremo: estimación libre";
                            else if (p.baremo_code) rowTitle = `Baremo: ${p.baremo_code}`;
                            return (
                            <tr
                              key={p._key || `p-${i}`}
                              className={`border-t border-[#E5E2D8] ${p.sin_baremo ? "bg-[#F7F6F1] text-[#9A988F]" : ""}`}
                              data-testid={`partida-row-${i}`}
                              title={rowTitle}
                            >
                              <td className="px-2 py-1">
                                <BaremoCombobox
                                  items={baremoItems}
                                  currentCode={p.baremo_code}
                                  currentDesc={p.concepto}
                                  baremoLabel={p.baremo_descripcion}
                                  sinBaremo={p.sin_baremo}
                                  testid={`partida-${i}-baremo`}
                                  onSelect={(it) => {
                                    updatePartida(i, "baremo_code", it.code || null);
                                    // Guardamos la descripción del baremo aparte del campo
                                    // libre. Así editar "Descripción libre" NO toca la
                                    // etiqueta del combobox (lo que pidió el usuario).
                                    updatePartida(i, "baremo_descripcion", it.descripcion || "");
                                    // Inicializamos concepto con la descripción del baremo
                                    // SOLO si el perito no había escrito nada libre todavía,
                                    // o si la concepto era exactamente la descripción anterior
                                    // del baremo (caso típico tras refresco de IA).
                                    if (!p.concepto || p.concepto === p.baremo_descripcion) {
                                      updatePartida(i, "concepto", it.descripcion || "");
                                    }
                                    updatePartida(i, "precio_unidad_eur", Number(it.precio || 0));
                                    updatePartida(i, "sin_baremo", false);
                                  }}
                                  onClear={() => {
                                    updatePartida(i, "baremo_code", null);
                                    updatePartida(i, "baremo_descripcion", "");
                                    updatePartida(i, "sin_baremo", true);
                                  }}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  value={p.concepto || ""}
                                  onChange={(e) => updatePartida(i, "concepto", e.target.value)}
                                  placeholder="Ej: parquet salón"
                                  className={`w-full bg-transparent px-1 py-1 rounded outline-none focus:bg-[#FBFAF6] ${p.sin_baremo ? "text-[#9A988F]" : "text-[#2C2C2A]"}`}
                                  data-testid={`partida-concepto-${i}`}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <select
                                  value={p.tipo === "contenido" ? "contenido" : "continente"}
                                  onChange={(e) => updatePartida(i, "tipo", e.target.value)}
                                  className={`w-full bg-transparent text-[12px] px-1 py-1 rounded outline-none focus:bg-[#FBFAF6] ${p.tipo === "contenido" ? "text-[#2E5AAC]" : "text-[#1D9E75]"}`}
                                  data-testid={`partida-${i}-tipo`}
                                >
                                  <option value="continente">Continente</option>
                                  <option value="contenido">Contenido</option>
                                </select>
                              </td>
                              <td className="px-2 py-1">
                                {p.fuente_documento ? (
                                  <div className="text-[10px] text-[#7A786F] truncate" title={`${p.fuente_documento}${p.linea_documento ? " · " + p.linea_documento : ""}`}>
                                    <span className="text-[#2E5AAC] font-medium">{p.fuente_documento.slice(0, 18)}</span>
                                    {p.precio_acorde_mercado === false && (
                                      <span className="ml-1 text-rose-700" title="Precio del documento fuera de mercado">⚠</span>
                                    )}
                                    {p.precio_acorde_mercado === true && (
                                      <span className="ml-1 text-[#1D9E75]" title="Precio acorde a mercado">✓</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-[#BAB7AA] italic">estimación</span>
                                )}
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  type="number" min="0" step="0.01"
                                  value={p.unidades ?? 0}
                                  onChange={(e) => updatePartida(i, "unidades", e.target.value === "" ? 0 : Number(e.target.value))}
                                  className="w-full bg-transparent text-right text-[#2C2C2A] px-1 py-1 rounded outline-none focus:bg-[#FBFAF6] font-mono"
                                  data-testid={`partida-uds-${i}`}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number" min="0" step="0.01"
                                    value={p.precio_unidad_eur ?? 0}
                                    onChange={(e) => updatePartida(i, "precio_unidad_eur", e.target.value === "" ? 0 : Number(e.target.value))}
                                    className="flex-1 min-w-0 bg-transparent text-right text-[#2C2C2A] px-1 py-1 rounded outline-none focus:bg-[#FBFAF6] font-mono"
                                    data-testid={`partida-eur-u-${i}`}
                                  />
                                  <span className="text-[#7A786F] text-[11px] shrink-0">€</span>
                                </div>
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  type="number" min="0" max="100" step="1"
                                  value={p.depreciacion_pct ?? 0}
                                  onChange={(e) => updatePartida(i, "depreciacion_pct", e.target.value === "" ? 0 : Number(e.target.value))}
                                  className="w-full bg-transparent text-right text-[#2C2C2A] px-1 py-1 rounded outline-none focus:bg-[#FBFAF6] font-mono"
                                  data-testid={`partida-dep-${i}`}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  type="number" min="0" max="100" step="1"
                                  value={p.mercado_pct ?? 0}
                                  onChange={(e) => updatePartida(i, "mercado_pct", e.target.value === "" ? 0 : Number(e.target.value))}
                                  className="w-full bg-transparent text-right text-[#2C2C2A] px-1 py-1 rounded outline-none focus:bg-[#FBFAF6] font-mono"
                                  data-testid={`partida-mkt-${i}`}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  type="number" min="0" max="100" step="1"
                                  value={p.iva_pct ?? 0}
                                  onChange={(e) => updatePartida(i, "iva_pct", e.target.value === "" ? 0 : Number(e.target.value))}
                                  className="w-full bg-transparent text-right text-[#2C2C2A] px-1 py-1 rounded outline-none focus:bg-[#FBFAF6] font-mono"
                                  data-testid={`partida-iva-${i}`}
                                />
                              </td>
                              <td className="px-2 py-1 text-right font-mono font-medium text-[#2C2C2A]" data-testid={`partida-total-${i}`}>
                                {formatEur(p.total_partida_eur)}
                              </td>
                              <td className="px-1 py-1 text-center">
                                <button
                                  onClick={() => removePartida(i)}
                                  className="text-[#7A786F] hover:text-[#B43F3F] p-1"
                                  title="Eliminar partida"
                                  data-testid={`partida-delete-${i}`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={addPartida} data-testid="add-partida-btn">
                      <ImagePlus className="h-3 w-3 mr-1" /> Añadir partida
                    </Button>
                    <Button size="sm" variant="outline" onClick={sumPartidasAsTotal} data-testid="sum-partidas-btn">
                      Calcular total ∑
                    </Button>
                    <Button size="sm" variant="outline" onClick={applyMarkup15} data-testid="markup-15-btn" title="Aplica +15% a cada partida (precio de mercado)">
                      +15 % mercado
                    </Button>
                    {valoracionDraft?.iva_aplicado ? (
                      <Button size="sm" variant="outline" onClick={removeIVA21} data-testid="iva-21-remove-btn" title="Quita el IVA del total" className="border-[#1D9E75] text-[#1D9E75]">
                        Quitar IVA 21 %
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={applyIVA21} data-testid="iva-21-btn" title="Añade 21% IVA al total">
                        +21 % IVA
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => copyText(buildValoracionPlainText(), "Valoración copiada al portapapeles")} data-testid="copy-valoracion-btn" className="ml-auto">
                      <Copy className="h-3 w-3 mr-1" /> Copiar valoración
                    </Button>
                  </div>
                </div>

                {/* Desglose IVA si aplicado */}
                {valoracionDraft.iva_aplicado && (
                  <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-3 text-sm font-mono space-y-0.5" data-testid="valoracion-iva-breakdown">
                    <div className="flex justify-between"><span className="text-[#7A786F]">Subtotal sin IVA:</span><span>{formatEur(valoracionDraft.subtotal_sin_iva)}</span></div>
                    <div className="flex justify-between"><span className="text-[#7A786F]">IVA (21 %):</span><span>{formatEur(valoracionDraft.iva_eur)}</span></div>
                    <div className="flex justify-between font-medium text-[#2C2C2A] border-t border-[#E5E2D8] pt-1 mt-1"><span>TOTAL con IVA:</span><span>{formatEur(valoracionDraft.total_estimado_eur)}</span></div>
                  </div>
                )}

                {/* Sublímites de póliza detectados y franquicia aplicada por la IA */}
                {(() => {
                  const limites = valoracionDraft.limites_aplicados || [];
                  const franq = valoracionDraft.franquicia_eur;
                  const totalNeto = valoracionDraft.total_neto_eur;
                  const tieneAlgo = limites.length > 0 || (franq != null && franq > 0) || (totalNeto != null && totalNeto !== valoracionDraft.total_estimado_eur);
                  if (!tieneAlgo) return null;
                  return (
                    <div className="border-2 border-[#2E5AAC] bg-[#EAF1FB] rounded-[12px] p-3 text-sm space-y-2" data-testid="valoracion-limites-block">
                      <div className="font-medium text-[#2E5AAC] flex items-center gap-1.5">
                        <ShieldCheck className="h-4 w-4" /> Sublímites y franquicia aplicados por la póliza
                      </div>
                      {limites.length > 0 && (
                        <div className="space-y-1.5">
                          {limites.map((lim, k) => {
                            const cap = (lim.bruto_eur || 0) > (lim.limite_eur || 0);
                            // Key estable: garantía + índice (cubre el caso raro
                            // de dos sublímites con el mismo nombre de garantía).
                            const rowKey = `${lim.garantia || "sin"}-${k}`;
                            return (
                              <div key={rowKey} className="bg-white border border-[#D6E0F0] rounded-[8px] p-2.5" data-testid={`limite-${k}`}>
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <div className="font-medium text-[#2C2C2A]">{lim.garantia}</div>
                                  <div className="text-[11px] text-[#7A786F] font-mono">{lim.base}</div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mt-1 text-[12px] font-mono">
                                  <div>
                                    <div className="text-[10px] text-[#7A786F] uppercase">Límite</div>
                                    <div className="text-[#2C2C2A]">{formatEur(lim.limite_eur)}</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] text-[#7A786F] uppercase">Bruto partidas</div>
                                    <div className={cap ? "text-[#B43F3F]" : "text-[#2C2C2A]"}>{formatEur(lim.bruto_eur)}</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] text-[#7A786F] uppercase">Aplicado</div>
                                    <div className="text-[#1D9E75] font-medium">{formatEur(lim.importe_aplicado)}</div>
                                  </div>
                                </div>
                                {lim.cita && (
                                  <div className="mt-1.5 text-[11px] italic text-[#5B5B58] border-l-2 border-[#D6E0F0] pl-2">«{lim.cita}»</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="border-t border-[#D6E0F0] pt-2 mt-1 text-sm font-mono space-y-0.5">
                        <div className="flex justify-between"><span className="text-[#7A786F]">Total estimado (bruto):</span><span>{formatEur(valoracionDraft.total_estimado_eur)}</span></div>
                        {franq != null && franq > 0 && (
                          <div className="flex justify-between"><span className="text-[#7A786F]">Franquicia aplicada:</span><span className="text-[#B43F3F]">− {formatEur(franq)}</span></div>
                        )}
                        {totalNeto != null && (
                          <div className="flex justify-between font-medium text-[#1D9E75] border-t border-[#D6E0F0] pt-1 mt-1">
                            <span>TOTAL NETO A INDEMNIZAR:</span>
                            <span data-testid="valoracion-total-neto">{formatEur(totalNeto)}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-[11px] text-[#5B5B58] italic">Los sublímites y la franquicia los detecta la IA leyendo el condicionado asignado al expediente. Revísalos antes de cerrar el informe.</p>
                    </div>
                  );
                })()}

                {/* Subtotales por tipo + indemnización tras infraseguro */}
                {(() => {
                  const partidas = valoracionDraft.partidas || [];
                  const sumByTipo = (tipo) => partidas
                    .filter(p => (p.tipo || "continente") === tipo)
                    .reduce((acc, p) => acc + Number(p.total_partida_eur || 0), 0);
                  const subCont = sumByTipo("continente");
                  const subContenido = sumByTipo("contenido");
                  const totalBase = subCont + subContenido;
                  const pr = preex?.result || {};
                  const cobCont = (pr.cobertura_continente_pct != null && pr.cobertura_continente_pct < 100)
                    ? Number(pr.cobertura_continente_pct) / 100 : null;
                  const cobContenido = (pr.cobertura_contenido_pct != null && pr.cobertura_contenido_pct < 100)
                    ? Number(pr.cobertura_contenido_pct) / 100 : null;
                  const indemCont = (cobCont != null && applyInfraCont) ? subCont * cobCont : subCont;
                  const indemContenido = (cobContenido != null && applyInfraContenido) ? subContenido * cobContenido : subContenido;
                  const totalIndem = indemCont + indemContenido;
                  const fmt = (n) => Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
                  const pct = (n) => Number(n * 100).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " %";
                  return (
                    <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[12px] p-3" data-testid="valoracion-subtotales">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-[#1D9E75]">Subtotal continente</div>
                          <div className="font-mono font-semibold text-[#1D9E75]" data-testid="subtotal-continente">{fmt(subCont)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-[#2E5AAC]">Subtotal contenido</div>
                          <div className="font-mono font-semibold text-[#2E5AAC]" data-testid="subtotal-contenido">{fmt(subContenido)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-[#7A786F]">Total partidas</div>
                          <div className="font-mono font-semibold text-[#2C2C2A]" data-testid="valoracion-total-base">{fmt(totalBase)}</div>
                        </div>
                        {(cobCont != null || cobContenido != null) && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wide text-[#D85A30]">Tras infraseguro</div>
                            <div className="font-mono font-semibold text-[#D85A30]" data-testid="valoracion-indemnizacion">{fmt(totalIndem)}</div>
                          </div>
                        )}
                      </div>
                      {(cobCont != null || cobContenido != null) && (
                        <div className="mt-3 pt-3 border-t border-[#E5E2D8] grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          {cobCont != null && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={applyInfraCont} onChange={(e) => setApplyInfraCont(e.target.checked)} className="accent-[#1D9E75] h-3.5 w-3.5" data-testid="toggle-infra-continente" />
                              <span>Aplicar infraseguro CONTINENTE ({pct(1 - cobCont)} demérito · cobertura {pct(cobCont)}){applyInfraCont && <span className="ml-2 font-mono text-[#1D9E75]">→ {fmt(indemCont)}</span>}</span>
                            </label>
                          )}
                          {cobContenido != null && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={applyInfraContenido} onChange={(e) => setApplyInfraContenido(e.target.checked)} className="accent-[#2E5AAC] h-3.5 w-3.5" data-testid="toggle-infra-contenido" />
                              <span>Aplicar infraseguro CONTENIDO ({pct(1 - cobContenido)} demérito · cobertura {pct(cobContenido)}){applyInfraContenido && <span className="ml-2 font-mono text-[#2E5AAC]">→ {fmt(indemContenido)}</span>}</span>
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Total + Rango */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Total estimado (€)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={valoracionDraft.total_estimado_eur ?? ""}
                      onChange={(e) => updateValoracionField("total_estimado_eur", e.target.value === "" ? null : Number(e.target.value))}
                      placeholder="—"
                      className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none font-mono"
                      data-testid="valoracion-total-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Rango estimado</label>
                    <input
                      type="text"
                      value={valoracionDraft.rango_estimado ?? ""}
                      onChange={(e) => updateValoracionField("rango_estimado", e.target.value || null)}
                      placeholder="ej: entre 1.200€ y 2.000€"
                      className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none"
                      data-testid="valoracion-rango-input"
                    />
                  </div>
                </div>

                {/* Observaciones */}
                <div>
                  <label className="text-xs font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Observaciones</label>
                  <textarea
                    value={valoracionDraft.observaciones ?? ""}
                    onChange={(e) => updateValoracionField("observaciones", e.target.value || null)}
                    placeholder="Supuestos del cálculo, limitaciones, etc."
                    className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm leading-relaxed p-3 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none resize-y"
                    rows={3}
                    data-testid="valoracion-observaciones"
                  />
                </div>

                <div className="text-[11px] text-[#7A786F] italic">
                  Fotografías analizadas por IA: {valoracion?.fotos_analizadas || 0} de {photos.length} disponibles. Esta valoración es orientativa y se incluirá en el webhook a Ekspertium al finalizar el expediente.
                </div>

                <div className="flex items-center gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={discardValoracionEdits} disabled={!valoracionDirty || valoracionLoading} data-testid="valoracion-discard-btn">
                    Descartar cambios
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveValoracion}
                    disabled={!valoracionDirty || valoracionLoading}
                    className="bg-[#D85A30] hover:bg-[#C04D27] text-white"
                    data-testid="valoracion-save-btn"
                  >
                    {valoracionLoading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    Guardar valoración
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* === Preexistencia e Infraseguro (IA) === */}
          <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6" data-testid="ai-preexistencia-block">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Calculator className="h-4 w-4 text-[#D85A30]" />
              <div className="font-medium text-[#2C2C2A]">Preexistencia e infraseguro · análisis IA del catastro</div>
              {preex?.updated_at && (
                <span className="text-[11px] text-[#7A786F] font-mono">
                  act. {new Date(preex.updated_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}
                </span>
              )}
              {preex && (
                <Button size="sm" variant="outline" onClick={eliminarPreexistencia} className="ml-auto border-[#E5E2D8]" data-testid="preex-delete-btn">
                  <Trash2 className="h-3 w-3 mr-1" /> Eliminar
                </Button>
              )}
            </div>
            <div className="text-xs text-[#7A786F] mb-3">
              Sube la <strong>nota descriptiva del catastro</strong> y el <strong>documento de apertura</strong> (PDF). La IA extraerá automáticamente las superficies por uso, la provincia y los capitales contratados, y calculará el valor real, el % de infraseguro y la indemnización con regla proporcional.
            </div>

            {/* === Dos zonas de drag & drop === */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <PreexDropZone
                label="Descripción catastral"
                hint="PDF · nota descriptiva del Catastro"
                file={preex?.catastro_doc}
                busy={preexUpCat}
                onFile={uploadCatastroFile}
                testid="preex-drop-catastro"
                extracted={preex?.extracted}
                ok={!!preex?.extracted?.m2_vivienda}
              />
              <PreexDropZone
                label="Documento de apertura"
                hint="PDF · datos de la póliza al abrir el siniestro"
                file={preex?.apertura_doc}
                busy={preexUpApe}
                onFile={uploadAperturaFile}
                testid="preex-drop-apertura"
                extracted={preex?.apertura_doc?.extracted}
                ok={!!(preex?.apertura_doc?.extracted && (preex.apertura_doc.extracted.capital_continente || preex.apertura_doc.extracted.capital_contenido))}
              />
            </div>

            {/* Mini-form: datos de póliza y daño */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Producto</label>
                <input type="text" value={preexForm.producto}
                  onChange={(e) => setPreexForm(f => ({ ...f, producto: e.target.value }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none font-mono"
                  data-testid="preex-producto" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Fecha contratación <span className="text-[10px]">(solo 1680)</span></label>
                <input type="date" value={preexForm.fecha_contratacion}
                  onChange={(e) => setPreexForm(f => ({ ...f, fecha_contratacion: e.target.value }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none"
                  data-testid="preex-fecha" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Tipo de vivienda</label>
                <select value={preexForm.tipo_inmueble}
                  onChange={(e) => setPreexForm(f => ({ ...f, tipo_inmueble: e.target.value }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none"
                  data-testid="preex-tipo-vivienda">
                  <option value="Piso">Piso</option>
                  <option value="Independiente">Independiente</option>
                  <option value="Adosada">Adosada</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Calidad</label>
                <select value={preexForm.calidad}
                  onChange={(e) => setPreexForm(f => ({ ...f, calidad: e.target.value }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none"
                  data-testid="preex-calidad">
                  <option value="Sencilla">Sencilla</option>
                  <option value="Estándar">Estándar</option>
                  <option value="Lujo">Lujo</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Uso</label>
                <select value={preexForm.uso}
                  onChange={(e) => setPreexForm(f => ({ ...f, uso: e.target.value }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none"
                  data-testid="preex-uso">
                  <option value="Principal">Principal (FC 1,0)</option>
                  <option value="Secundaria">Secundaria (FC 0,5)</option>
                  <option value="Cedida en alquiler">Alquiler (FC 0,6)</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Capital continente (€)</label>
                <input type="text" inputMode="decimal" value={preexForm.capital_continente}
                  onChange={(e) => setPreexForm(f => ({ ...f, capital_continente: parseNumLocale(e.target.value) }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none font-mono"
                  data-testid="preex-cap-cont" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Capital contenido (€)</label>
                <input type="text" inputMode="decimal" value={preexForm.capital_contenido}
                  onChange={(e) => setPreexForm(f => ({ ...f, capital_contenido: parseNumLocale(e.target.value) }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none font-mono"
                  data-testid="preex-cap-contenido" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">m² declarados en póliza</label>
                <input type="text" inputMode="decimal" value={preexForm.m2_declarados_poliza}
                  onChange={(e) => setPreexForm(f => ({ ...f, m2_declarados_poliza: parseNumLocale(e.target.value) }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none font-mono"
                  data-testid="preex-m2-decl" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Nº ocupantes</label>
                <input type="number" min="0" step="1" value={preexForm.ocupantes}
                  onChange={(e) => setPreexForm(f => ({ ...f, ocupantes: parseInt(e.target.value || "0", 10) || 0 }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none font-mono"
                  data-testid="preex-ocupantes" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Daño reclamado (€)</label>
                <input type="text" inputMode="decimal" value={preexForm.danno_eur}
                  onChange={(e) => setPreexForm(f => ({ ...f, danno_eur: parseNumLocale(e.target.value) }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none font-mono"
                  data-testid="preex-danno" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Franquicia (€)</label>
                <input type="text" inputMode="decimal" value={preexForm.franquicia_eur}
                  onChange={(e) => setPreexForm(f => ({ ...f, franquicia_eur: parseNumLocale(e.target.value) }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none font-mono"
                  data-testid="preex-franq" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#7A786F] uppercase tracking-wide block mb-1">Tipo de daño</label>
                <select value={preexForm.tipo_danno}
                  onChange={(e) => setPreexForm(f => ({ ...f, tipo_danno: e.target.value }))}
                  className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none"
                  data-testid="preex-tipo-danno">
                  <option value="continente">Continente</option>
                  <option value="contenido">Contenido</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end mt-3">
              <Button size="sm" onClick={calcularPreexistencia} disabled={preexLoading || !preex?.extracted?.m2_vivienda}
                className="bg-[#D85A30] hover:bg-[#C04D27] text-white" data-testid="preex-calc-btn">
                {preexLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Calculator className="h-3 w-3 mr-1" />}
                Recalcular
              </Button>
            </div>

            {preex?.result && (
              <div className="mt-4 space-y-3" data-testid="preex-result">
                {/* Datos extraídos por IA del catastro */}
                {preex.extracted && (
                  <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-3 text-sm">
                    <div className="text-[10px] uppercase tracking-wide text-[#7A786F] mb-1">Datos extraídos del catastro</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs">
                      <div><span className="text-[#7A786F]">Provincia:</span> <strong>{preex.extracted.provincia || "—"}</strong> <span className="text-[#7A786F]">(Zona {preex.result.zona})</span></div>
                      <div><span className="text-[#7A786F]">Tipo:</span> <strong>{preex.extracted.tipo_inmueble || preex.result.tipo}</strong></div>
                      <div><span className="text-[#7A786F]">Vivienda:</span> <strong>{preex.extracted.m2_vivienda} m²</strong></div>
                      <div><span className="text-[#7A786F]">Trastero:</span> <strong>{preex.extracted.m2_trastero} m²</strong></div>
                      <div><span className="text-[#7A786F]">Garaje:</span> <strong>{preex.extracted.m2_garaje} m²</strong></div>
                      <div><span className="text-[#7A786F]">Z. comunes:</span> <strong>{preex.extracted.m2_zonas_comunes} m²</strong></div>
                      <div><span className="text-[#7A786F]">Otras zonas:</span> <strong>{preex.extracted.m2_otras_zonas} m²</strong></div>
                      {preex.extracted.ano_construccion && <div><span className="text-[#7A786F]">Año:</span> <strong>{preex.extracted.ano_construccion}</strong></div>}
                    </div>
                    {preex.extracted.desglose_usos?.length > 0 && (
                      <div className="text-[11px] text-[#7A786F] mt-2 italic">
                        Desglose original: {preex.extracted.desglose_usos.map(d => `${d.uso} (${d.m2} m²)`).join(" · ")}
                      </div>
                    )}
                    {preex.extracted.notas && (
                      <div className="text-[11px] text-[#D85A30] mt-1">⚠️ {preex.extracted.notas}</div>
                    )}
                  </div>
                )}

                {/* Resumen cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-3">
                    <div className="text-[10px] uppercase tracking-wide text-[#7A786F]">Continente (valor real)</div>
                    <div className="font-semibold text-[#2C2C2A]" data-testid="preex-total-cont">
                      {Number(preex.result.preexistencia_continente || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </div>
                    <div className="text-xs text-[#7A786F]">
                      {preex.result.infraseguro_continente_pct ? `Infraseguro ${preex.result.infraseguro_continente_pct} %` : "Sin infraseguro"}
                    </div>
                  </div>
                  <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-3">
                    <div className="text-[10px] uppercase tracking-wide text-[#7A786F]">Contenido (valor real)</div>
                    <div className="font-semibold text-[#2C2C2A]" data-testid="preex-total-contenido">
                      {Number(preex.result.preexistencia_contenido || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </div>
                    <div className="text-xs text-[#7A786F]">
                      {preex.result.infraseguro_contenido_pct ? `Infraseguro ${preex.result.infraseguro_contenido_pct} %` : "Sin infraseguro"}
                    </div>
                  </div>
                  {preex.result.indemnizacion_eur != null && (
                    <div className="bg-[#E6F5EF] border border-transparent rounded-[10px] p-3">
                      <div className="text-[10px] uppercase tracking-wide text-[#1D9E75]">Indemnización (regla proporcional)</div>
                      <div className="font-semibold text-[#1D9E75]" data-testid="preex-indemn">
                        {Number(preex.result.indemnizacion_eur || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </div>
                      <div className="text-xs text-[#1D9E75]">Factor {preex.result.factor_proporcional_pct} %</div>
                    </div>
                  )}
                </div>

                {/* Informe completo */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-wide text-[#7A786F]">Informe completo</div>
                    <Button size="sm" variant="outline" onClick={copiarInformePreex} className="border-[#E5E2D8]" data-testid="preex-copy-btn">
                      <Copy className="h-3 w-3 mr-1" /> Copiar informe
                    </Button>
                  </div>
                  <textarea
                    readOnly
                    value={preex.result.informe_texto || ""}
                    rows={18}
                    className="w-full font-mono text-xs bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-3 outline-none resize-y"
                    data-testid="preex-informe"
                  />
                </div>

                <div className="text-[11px] text-[#7A786F] italic">
                  Los capitales recomendados y el % de infraseguro se incluyen automáticamente al pulsar <strong>Copiar valoración</strong>.
                </div>
              </div>
            )}
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
}


/**
 * Editor inline de la descripción de una foto.
 * - Muestra la descripción actual (IA o manual) con un badge.
 * - Click sobre el texto → modo edición con textarea (240-600 chars).
 * - Guardar en blur (PUT /api/photos/{id}/description).
 * - Si el perito borra todo y guarda, vuelve al estado "sin descripción" y
 *   la próxima ejecución de /ai-report propondrá una nueva descripción IA.
 */
function PhotoDescription({ photo, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(photo.description || "");
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (!editing) setDraft(photo.description || "");
  }, [photo.description, editing]);

  const save = async () => {
    if (draft === (photo.description || "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/photos/${photo.id}/description`, { description: draft });
      onSaved && onSaved({
        description: r.data?.description ?? null,
        description_source: r.data?.description_source ?? null,
      });
      toast.success("Descripción guardada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la descripción");
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const hasDesc = !!(photo.description && photo.description.trim());
  const isManual = photo.description_source === "manual";

  if (editing) {
    return (
      <div className="pt-1.5">
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          maxLength={600}
          rows={3}
          placeholder="Describe brevemente lo que se ve en la foto (la IA lo rellenará al procesar el expediente si lo dejas vacío)…"
          className="w-full text-[11px] leading-snug p-2 rounded-md border border-[#D85A30]/60 outline-none focus:border-[#D85A30] bg-white"
          data-testid={`photo-desc-textarea-${photo.id}`}
        />
        <div className="flex items-center justify-between mt-0.5">
          <span className="text-[10px] text-[#7A786F]">{draft.length}/600</span>
          {saving && <span className="text-[10px] text-[#D85A30]">Guardando…</span>}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`pt-1 mt-1 border-t border-[#E5E2D8] text-left w-full block text-[11px] leading-snug hover:bg-[#FBFAF6] rounded transition-colors px-1 -mx-1`}
      data-testid={`photo-desc-btn-${photo.id}`}
    >
      {hasDesc ? (
        <>
          <div className="text-[#2C2C2A]">{photo.description}</div>
          <div className="mt-0.5">
            <span className={`inline-block text-[9px] uppercase tracking-wide px-1.5 py-[1px] rounded ${isManual ? "bg-[#1D9E75]/15 text-[#1D9E75]" : "bg-[#2E5AAC]/15 text-[#2E5AAC]"}`}>
              {isManual ? "Editado por perito" : "IA"}
            </span>
          </div>
        </>
      ) : (
        <span className="text-[#7A786F] italic">Sin descripción — pulsa para añadir o procesa con IA</span>
      )}
    </button>
  );
}
